import logger from '../utils/logger';
import React, { useState, useRef, useEffect } from 'react';
import { Send, Brain, Download, Play, Square, Copy, FileDown, Plus, Globe, BarChart3, ChevronDown, ChevronUp, History, Clock, MessageSquare, Zap, Eye, Menu, X, Trash2, Edit3, AlertCircle, Paperclip } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import TokenBadge from './billing/TokenBadge';
import ThinkingAgentV2 from './ThinkingAgentV2';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Pie, Scatter } from 'react-chartjs-2';
import { downloadPDF } from '../utils/pdfExportV3';
import PDFPreviewV3 from './PDFPreviewV3';
import InsufficientTokensModal from './billing/InsufficientTokensModal';
import { useTokens } from '../hooks/useTokens';
import { API_URL } from '../lib/config';
import { authFetch, getAuthHeaders } from '../utils/api-headers';
import { useTheme } from '@/contexts/ThemeContext';
import CreationSpark from './brand/CreationSpark';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, ArcElement);

interface Message {
  id: string;
 role: 'user' | 'assistant' | 'system';
  content: string;
  thinking?: string;
  timestamp: Date;
 metadata?: {
 isError?: boolean;
 canRetry?: boolean;
 originalMessage?: string;
 originalFiles?: any[];
    attachedFile?: {
      name: string;
      size: number;
      type?: string;
      fileKey?: string;
      fileId?: string;
      orgPath?: string;
    };
 };
}

// Composant pour les graphiques interactifs - ULTRA-FLEXIBLE avec useMemo pour éviter vibrations
const InteractiveChart = React.memo(({ type, data, title, insight }: { type: string, data: any, title: string, insight?: string }) => {
 const { theme } = useTheme();
 const isDark = theme === 'dark';
 
 // Configuration tooltip selon le thème
 const tooltipConfig = {
 backgroundColor: isDark ? '#1f2937' : '#ffffff',
 titleColor: isDark ? '#ffffff' : '#000000',
 bodyColor: isDark ? '#ffffff' : '#000000',
 borderColor: isDark ? '#374151' : '#e5e7eb',
 borderWidth: 1,
 padding: 12,
 cornerRadius: 8,
 displayColors: true,
 boxPadding: 6
 };
 
 // FIX: Utiliser useMemo pour éviter re-calculs constants (cause des vibrations)
 const normalizedData = React.useMemo(() => {
 if (!Array.isArray(data)) return [];
 
 return data.map((d: any) => {
 if (!d || typeof d !== 'object') return null;
 
 // Extraire le label (premier champ texte trouvé)
 const labelKey = Object.keys(d).find(k => 
 typeof d[k] === 'string' || k.toLowerCase().includes('label') || k.toLowerCase().includes('name') || 
 k.toLowerCase().includes('mois') || k.toLowerCase().includes('produit') || k.toLowerCase().includes('segment')
 );
 const label = labelKey ? d[labelKey] : 'Sans nom';
 
 // Extraire la valeur (premier champ numérique trouvé) - ROBUSTE comme PDF
 const valueKey = Object.keys(d).find(k => 
   typeof d[k] === 'number' || 
   k.toLowerCase().includes('value') || 
   k.toLowerCase().includes('ca') || 
   k.toLowerCase().includes('amount') || 
   k.toLowerCase().includes('potentiel') || 
   k.toLowerCase().includes('montant') ||
   k.toLowerCase().includes('total') ||
   k.toLowerCase().includes('budget')
 );
 const value = valueKey ? (typeof d[valueKey] === 'number' ? d[valueKey] : parseFloat(d[valueKey]) || 0) : 0;
 
 return { label, value };
 }).filter(d => d && d.value > 0);
 }, [data]); // Recalculer uniquement si data change
 
 if (normalizedData.length === 0) {
 return null; // Ne pas afficher de message d'erreur, juste ignorer
 }
 
 const cleanData = normalizedData;

  if (type === 'pie' || type === 'doughnut' || type === 'polarArea') {
    const chartData = {
      labels: cleanData.map((d: any) => d.label || d.produit || d.name || 'Sans nom'),
      datasets: [{
        data: cleanData.map((d: any) => d.value || d.ca || d.amount || 0),
        backgroundColor: [
          '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5A2B', '#6B7280', '#EC4899', '#14B8A6', '#F97316'
        ],
        borderWidth: 0,
        hoverBorderWidth: 2,
        hoverBorderColor: '#ffffff'
      }]
    };
    
    return (
 <div className="my-6 p-4 bg-theme-elevated rounded-xl shadow-theme-sm border border-theme-border">
        <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-theme-primary">{title}</h3>
 <div className="text-xs text-theme-tertiary bg-theme-hover px-2 py-1 rounded">
            {cleanData.length} éléments
          </div>
        </div>
        <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
          <Pie data={chartData} options={{ 
            responsive: true, 
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom' as const,
                labels: {
                  boxWidth: 12,
                  padding: 15
                }
 },
 tooltip: {
 backgroundColor: tooltipConfig.backgroundColor,
 titleColor: tooltipConfig.titleColor,
 bodyColor: tooltipConfig.bodyColor,
 borderColor: tooltipConfig.borderColor,
 borderWidth: tooltipConfig.borderWidth,
 padding: tooltipConfig.padding,
 cornerRadius: tooltipConfig.cornerRadius,
 displayColors: tooltipConfig.displayColors,
 boxPadding: tooltipConfig.boxPadding
              }
            }
          }} />
        </div>
      </div>
    );
  }
 
 // SCATTER CHART (Matrice 2D)
 if (type === 'scatter') {
 // Détecter le type de données (CA/Marge ou Risque/Impact)
 const firstPoint = data[0] || {};
 const hasRiskData = 'risk_probability' in firstPoint || 'financial_impact' in firstPoint;
 
 const scatterData = {
 datasets: [{
 label: title,
 data: data.map((d: any) => ({
 x: d.risk_probability || d.ca || d.x || 0,
 y: d.financial_impact || d.marge || d.y || 0,
 label: d.phase || d.produit || d.label || d.name || ''
 })),
 backgroundColor: 'rgba(139, 92, 246, 0.6)',
 borderColor: 'rgba(139, 92, 246, 1)',
 pointRadius: 8,
 pointHoverRadius: 12
 }]
 };
 
 const xAxisLabel = hasRiskData ? 'Risque (%)' : 'CA (M€)';
 const yAxisLabel = hasRiskData ? 'Impact (€)' : 'Marge (%)';
 
 return (
 <div className="my-6 p-4 bg-theme-elevated rounded-xl shadow-theme-sm border border-theme-border">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-theme-primary">{title}</h3>
 <div className="text-xs text-theme-tertiary bg-theme-hover px-2 py-1 rounded">
 Scatter · {data.length} points
 </div>
 </div>
 <div style={{ height: '400px' }}>
 <Scatter 
 data={scatterData as any} 
 options={{ 
 responsive: true, 
 maintainAspectRatio: false,
 scales: {
 x: {
 type: 'linear',
 title: { display: true, text: xAxisLabel },
 beginAtZero: false
 },
 y: {
 type: 'linear',
 title: { display: true, text: yAxisLabel },
 beginAtZero: false
 }
 },
 plugins: {
 legend: { display: false },
 tooltip: {
 backgroundColor: tooltipConfig.backgroundColor,
 titleColor: tooltipConfig.titleColor,
 bodyColor: tooltipConfig.bodyColor,
 borderColor: tooltipConfig.borderColor,
 borderWidth: tooltipConfig.borderWidth,
 padding: tooltipConfig.padding,
 cornerRadius: tooltipConfig.cornerRadius,
 displayColors: tooltipConfig.displayColors,
 boxPadding: tooltipConfig.boxPadding,
 callbacks: {
 label: (context: any) => {
 const point = data[context.dataIndex];
 if (hasRiskData) {
 return [
 `${point.phase || point.label}`,
 `Risque: ${point.risk_probability}%`,
 `Impact: ${point.financial_impact}€`,
 point.budget ? `Budget: ${point.budget}€` : ''
 ].filter(Boolean);
 } else {
 return [
 `${point.produit || point.label}`,
 `CA: ${point.ca || point.x}M€`,
 `Marge: ${point.marge || point.y}%`,
 point.opportunite ? `${point.opportunite}` : ''
 ].filter(Boolean);
 }
 }
 }
 }
 }
 }} 
 />
 </div>
 {insight && (
 <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-600 text-sm text-gray-900 dark:text-white">
 <strong className="text-purple-700 dark:text-purple-300">Insight:</strong> {insight.replace('INSIGHT ACTIONNABLE:', '').trim()}
 </div>
 )}
      </div>
    );
  }
  
  // WATERFALL CHART - Graphique en cascade (changements cumulatifs)
  if (type === 'waterfall') {
    // Calculer les valeurs cumulatives pour waterfall
    let cumulative = 0;
    const waterfallData = cleanData.map((d: any, index: number) => {
      const value = d.value || d.ca || d.amount || 0;
      const start = cumulative;
      cumulative += value;
      return {
        label: d.label || d.name || `Étape ${index + 1}`,
        start,
        end: cumulative,
        value,
        isPositive: value >= 0
      };
    });

    const chartData = {
      labels: waterfallData.map((d: any) => d.label),
      datasets: [{
        label: 'Valeur',
        data: waterfallData.map((d: any) => d.value),
        backgroundColor: waterfallData.map((d: any) => 
          d.isPositive ? 'rgba(16, 185, 129, 0.8)' : 'rgba(239, 68, 68, 0.8)'
        ),
        borderColor: waterfallData.map((d: any) => 
          d.isPositive ? 'rgba(16, 185, 129, 1)' : 'rgba(239, 68, 68, 1)'
        ),
        borderWidth: 1,
        borderRadius: 4
      }]
    };

    return (
      <div className="my-6 p-4 bg-theme-elevated rounded-xl shadow-theme-sm border border-theme-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-theme-primary">{title}</h3>
          <div className="text-xs text-theme-tertiary bg-theme-hover px-2 py-1 rounded">
            Waterfall · {cleanData.length} étapes
          </div>
        </div>
        <div style={{ height: '300px' }}>
          <Bar data={chartData} options={{ 
            responsive: true, 
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: tooltipConfig.backgroundColor,
                titleColor: tooltipConfig.titleColor,
                bodyColor: tooltipConfig.bodyColor,
                borderColor: tooltipConfig.borderColor,
                borderWidth: tooltipConfig.borderWidth,
                padding: tooltipConfig.padding,
                cornerRadius: tooltipConfig.cornerRadius,
                displayColors: tooltipConfig.displayColors,
                boxPadding: tooltipConfig.boxPadding,
                callbacks: {
                  label: (context: any) => {
                    const item = waterfallData[context.dataIndex];
                    return [
                      `Valeur: ${item.value >= 0 ? '+' : ''}${item.value}`,
                      `Cumul: ${item.end}`
                    ];
                  }
                }
              }
            },
            scales: {
              y: { beginAtZero: true }
            }
          }} />
        </div>
      </div>
    );
  }

  // SUPPORT COMPLET: bar, line, horizontal_bar, chart, area, stacked_bar, radar, bubble
  if (type === 'bar' || type === 'line' || type === 'horizontal_bar' || type === 'chart' || type === 'area' || type === 'stacked_bar' || type === 'radar' || type === 'bubble' || type === 'gantt') {
    const chartData = {
      labels: cleanData.map((d: any) => d.label || d.mois || d.name || 'Sans nom'),
      datasets: [{
        label: 'Valeur',
        data: cleanData.map((d: any) => d.value || 0), // Utiliser normalizedData qui a déjà extrait la valeur
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1,
        borderRadius: 4
      }]
    };
    
    return (
 <div className="my-6 p-4 bg-theme-elevated rounded-xl shadow-theme-sm border border-theme-border">
        <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-theme-primary">{title}</h3>
 <div className="text-xs text-theme-tertiary bg-theme-hover px-2 py-1 rounded">
            {cleanData.length} points
          </div>
        </div>
        <div style={{ height: '300px' }}>
          <Bar data={chartData} options={{ 
            responsive: true, 
            maintainAspectRatio: false,
            indexAxis: type === 'horizontal_bar' ? 'y' : 'x', // Horizontal si demandé
            plugins: {
              legend: {
                display: false
 },
 tooltip: {
 backgroundColor: tooltipConfig.backgroundColor,
 titleColor: tooltipConfig.titleColor,
 bodyColor: tooltipConfig.bodyColor,
 borderColor: tooltipConfig.borderColor,
 borderWidth: tooltipConfig.borderWidth,
 padding: tooltipConfig.padding,
 cornerRadius: tooltipConfig.cornerRadius,
 displayColors: tooltipConfig.displayColors,
 boxPadding: tooltipConfig.boxPadding
              }
            },
            scales: {
              y: {
                beginAtZero: true
              }
            }
          }} />
        </div>
      </div>
    );
  }
  
  // FALLBACK: Si le type n'est pas reconnu, essayer de le normaliser
  // Types radar/bubble → bar, etc.
  const normalizedType = 
    type === 'radar' || type === 'bubble' || type === 'stacked_bar' ? 'bar' :
    type === 'doughnut' || type === 'polarArea' ? 'pie' :
    type === 'area' ? 'line' :
    'bar'; // Fallback par défaut
  
  // Si on arrive ici, c'est qu'on n'a pas géré le type - utiliser le type normalisé
  logger.warn(`[FluidChat] Type de graphique "${type}" non géré explicitement, conversion en "${normalizedType}"`);
  
  // Récursion avec le type normalisé
  return <InteractiveChart type={normalizedType} data={data} title={title} insight={insight} />;
});

interface FluidChatProps {
  className?: string;
  onAnalysisComplete?: (analysis: any) => void;
  sessionId?: string;
  onSessionCreated?: (sessionId: string) => void;
}

const FluidChat: React.FC<FluidChatProps> = ({ 
  className = '', 
  onAnalysisComplete, 
  sessionId: providedSessionId,
  onSessionCreated 
}) => {
  const { user } = useAuth();
 const { balance, hasEnoughTokens, refresh: refreshTokens } = useTokens();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingMode, setThinkingMode] = useState(false);
  const [webSearchMode, setWebSearchMode] = useState(false);
  const [chartsMode, setChartsMode] = useState(true);
  const [quickMode, setQuickMode] = useState(false);
  const [currentThinking, setCurrentThinking] = useState('');
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [thinkingCollapsed, setThinkingCollapsed] = useState<{ [key: string]: boolean }>({});
 const [showPDFPreview, setShowPDFPreview] = useState(false);
 const [showInsufficientTokensModal, setShowInsufficientTokensModal] = useState(false);
 const [requiredTokensForRequest, setRequiredTokensForRequest] = useState(0);
 
 // UX: États pour les modales élégantes
 const [deleteModal, setDeleteModal] = useState<{ show: boolean; sessionId: string; sessionTitle: string }>({
 show: false,
 sessionId: '',
 sessionTitle: ''
 });
 const [renameModal, setRenameModal] = useState<{ show: boolean; sessionId: string; currentTitle: string }>({
 show: false,
 sessionId: '',
 currentTitle: ''
 });
 const [renameInput, setRenameInput] = useState('');
 
 // AGENTS AUTONOMES (activés automatiquement par thinking mode si contexte complexe)
 const [agentMode, setAgentMode] = useState(false); // Auto-activé, pas manuel
 const [agentSteps, setAgentSteps] = useState<any[]>([]);
 const [agentPaused, setAgentPaused] = useState(false);
 const [needsConfirmation, setNeedsConfirmation] = useState(false);
 const [confirmationMessage, setConfirmationMessage] = useState('');
 
 // PROTECTION ANTI-DOUBLE GENERATION D'INSIGHTS
 // Stocke les IDs de messages déjà traités, pas le contenu (plus précis)
 const insightsGeneratedForMessages = useRef<Set<string>>(new Set());
 
 // PERSISTANCE SESSION : Initialiser à null, sera chargé par useEffect
 const [currentSessionId, setCurrentSessionId] = useState<string | null>(providedSessionId || null);
  
  // WRAPPER PERSISTANCE : Sauvegarder la session active
  const updateCurrentSession = (sessionId: string | null) => {
    setCurrentSessionId(sessionId);
    if (sessionId && !sessionId.startsWith('temp-') && !sessionId.startsWith('ephemeral-')) {
      localStorage.setItem('clairify_active_chat_session', sessionId);
    } else if (!sessionId) {
      localStorage.removeItem('clairify_active_chat_session');
    }
  };
  
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isEphemeral, setIsEphemeral] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // États pour inline rename/delete
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
 const abortControllerRef = useRef<AbortController | null>(null); // Pour annuler les requêtes
 const streamingMessageIdRef = useRef<string | null>(null); // Pour éviter duplications de messages en streaming
 const isSendingRef = useRef<boolean>(false); // Protection anti-double-envoi

 // FIX: Un seul useEffect pour le chargement initial (voir plus bas)

  // NETTOYAGE DES SESSIONS TEMPORAIRES NON UTILISÉES
  useEffect(() => {
    return () => {
      // Cleanup: Supprimer session temporaire si aucun message envoyé
      if (currentSessionId && currentSessionId.startsWith('temp-') && messages.length === 0) {
        setChatHistory(prev => prev.filter(session => session.id !== currentSessionId));
      }
    };
  }, [currentSessionId, messages.length]);

  // Générer des noms de conversation personnalisés
   // Générer des noms de conversation intelligents basés sur le contenu
 // Fonction pour extraire un titre à partir de mots-clés (fallback rapide)
 const generateConversationTitleFallback = (firstMessage: string): string => {
       const content = firstMessage.toLowerCase();
       const subjects = [
 { keywords: ['vente', 'ca', 'chiffre', 'affaire', 'commercial'], title: 'Analyse Ventes' },
 { keywords: ['finance', 'budget', 'cout', 'prix'], title: 'Analyse Finance' },
 { keywords: ['client', 'customer', 'satisfaction'], title: 'Analyse Client' },
 { keywords: ['marketing', 'pub', 'campagne'], title: 'Analyse Marketing' },
 { keywords: ['data', 'csv', 'excel', 'fichier'], title: 'Analyse Data' },
 ];
 
       for (const subject of subjects) {
         if (subject.keywords.some(keyword => content.includes(keyword))) {
 return subject.title;
 }
 }
 
 // Dernière option : premiers mots
 const words = firstMessage.split(' ').filter(w => w.length > 3).slice(0, 3).join(' ');
 return words.length > 8 ? `${words.substring(0, 30)}...` : 'Nouvelle analyse';
   };
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // État pour tracker si l'utilisateur a scrollé manuellement
  const userScrolledRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = (force = false) => {
 // Utiliser scrollIntoView uniquement si on n'est pas en train de scroller manuellement
 if (messagesEndRef.current) {
 const container = messagesEndRef.current.parentElement;
 if (container) {
 const scrollHeight = container.scrollHeight;
 const scrollTop = container.scrollTop;
 const clientHeight = container.clientHeight;
 const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
 const isNearBottom = distanceFromBottom < 150; // Threshold de 150px

 // Scroll automatiquement SEULEMENT si :
 // 1. Force est true (appel explicite) ET on est proche du bas
 // 2. On est déjà proche du bas ET l'utilisateur n'a pas scrollé manuellement
 // 3. L'utilisateur est revenu en bas (réinitialise le flag)
 if (isNearBottom) {
   // Si l'utilisateur est revenu en bas, réinitialiser le flag
   if (userScrolledRef.current) {
     userScrolledRef.current = false;
   }
 }

 // Scroll seulement si on est proche du bas OU si force ET proche du bas
 if ((force && isNearBottom) || (!userScrolledRef.current && isNearBottom && isLoading)) {
   // Utiliser scrollTop pour un scroll instantané pendant le streaming
   if (force && isLoading) {
     container.scrollTop = container.scrollHeight;
   } else {
     messagesEndRef.current.scrollIntoView({ behavior: force ? 'auto' : 'smooth' });
   }
 }
 }
 }
 };

 // Détecter le scroll manuel de l'utilisateur
 useEffect(() => {
 const container = messagesEndRef.current?.parentElement;
 if (!container) return;

 const handleScroll = () => {
   const scrollHeight = container.scrollHeight;
   const scrollTop = container.scrollTop;
   const clientHeight = container.clientHeight;
   const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
   const isNearBottom = distanceFromBottom < 150;

   // Si l'utilisateur scroll vers le haut (loin du bas), marquer comme scroll manuel
   if (!isNearBottom) {
     userScrolledRef.current = true;
     
     // Nettoyer le timeout précédent
     if (scrollTimeoutRef.current) {
       clearTimeout(scrollTimeoutRef.current);
     }
     
     // Réinitialiser le flag après 2 secondes d'inactivité si l'utilisateur revient en bas
     scrollTimeoutRef.current = setTimeout(() => {
       if (container) {
         const newDistance = container.scrollHeight - container.scrollTop - container.clientHeight;
         if (newDistance < 150) {
           userScrolledRef.current = false;
         }
       }
     }, 2000);
   } else {
     // Si l'utilisateur est revenu en bas, réinitialiser immédiatement
     userScrolledRef.current = false;
     if (scrollTimeoutRef.current) {
       clearTimeout(scrollTimeoutRef.current);
     }
   }
 };

 container.addEventListener('scroll', handleScroll, { passive: true });

 return () => {
   container.removeEventListener('scroll', handleScroll);
   if (scrollTimeoutRef.current) {
     clearTimeout(scrollTimeoutRef.current);
   }
 };
 }, []);

 // Scroll automatique pendant le streaming - SEULEMENT si l'utilisateur est proche du bas
 useEffect(() => {
 if (isLoading && !userScrolledRef.current) {
   // Petit délai pour laisser le DOM se mettre à jour
   const timeoutId = setTimeout(() => {
     scrollToBottom(true);
   }, 100);
   
   return () => clearTimeout(timeoutId);
 }
 }, [messages, isLoading, currentThinking]);

 // PROTECTION: Éviter les appels multiples simultanés
 const isLoadingSessionsRef = useRef(false);
 const isEditingRef = useRef(false); // Protection pendant édition

  // Fonction pour charger l'historique des sessions
  const loadChatHistory = async () => {
 // PROTECTION BOUCLE INFINIE: Si déjà en cours, ignorer
 if (isLoadingSessionsRef.current) {
 logger.warn('[PROTECTION] loadChatHistory déjà en cours, ignoré');
 return;
 }
 
 // SÉCURITÉ CRITIQUE: Vérifier authentification AVANT toute requête
 if (!user?.id) {
 logger.error('[SÉCURITÉ] loadChatHistory: user.id manquant - BLOQUÉ');
 setChatHistory([]); // Vider la liste si pas authentifié
 return;
 }
 
 logger.log('[SÉCURITÉ] Chargement liste sessions avec JWT', { 
 userId: user.id,
 timestamp: new Date().toISOString()
 });
 
 try {
 isLoadingSessionsRef.current = true; // Verrouiller
      setHistoryLoading(true);
 
 const fullUrl = `${API_URL}/api/chat/sessions`;
 logger.log('[API] Envoi requête GET:', fullUrl);
 
 // SÉCURITÉ: JWT automatique via authFetch
 // BACKEND DOIT FILTRER: WHERE user_id = JWT.user_id
 
 // ⏱ Timeout de 60 secondes (cold start Scaleway peut prendre jusqu'à 60s)
 const timeoutPromise = new Promise((_, reject) => 
 setTimeout(() => reject(new Error('Timeout après 60s - Backend trop lent !')), 60000)
 );
 
 const response = await Promise.race([
 authFetch(fullUrl),
 timeoutPromise
 ]) as Response;
 
 logger.log('[API] Réponse reçue:', {
 status: response.status,
 statusText: response.statusText,
 ok: response.ok
 });
      
      if (response.ok) {
 const data = await response.json();
 logger.log('[API] Données reçues:', {
 sessionsCount: data.sessions?.length || 0,
 rawData: data
 });
 
 const { sessions } = data;
 
 if (!sessions || !Array.isArray(sessions)) {
 logger.error('[API] Format de réponse invalide - sessions n\'est pas un tableau');
 setChatHistory([]);
 return;
 }
        
        // Ajouter un compteur de messages pour filtrer les conversations vides
        const sessionsWithCount = sessions.map((session: any) => ({
          ...session,
          message_count: session.message_count || 0
        }));
        
 logger.log('[API] Sessions chargées avec succès:', sessionsWithCount.length);
        setChatHistory(sessionsWithCount.slice(0, 20)); // Limiter à 20 sessions récentes
      } else {
 // Lire le corps de la réponse pour diagnostiquer l'erreur
 let errorBody = 'Pas de détails';
 try {
 errorBody = await response.text();
 } catch (e) {
 errorBody = 'Impossible de lire le corps de l\'erreur';
 }
 
 logger.error('[API] Erreur HTTP:', {
 status: response.status,
 statusText: response.statusText,
 body: errorBody
 });
 
 setChatHistory([]);
 }
 } catch (error: unknown) {
 const errorMsg = error instanceof Error ? error.message : String(error);
 logger.error('[API] Exception lors du chargement:', {
 error: errorMsg,
 type: error instanceof Error ? error.constructor.name : typeof error,
 stack: error instanceof Error ? error.stack : undefined
 });
 
 setChatHistory([]);
    } finally {
      setHistoryLoading(false);
 isLoadingSessionsRef.current = false; // Déverrouiller
 logger.log(' [API] Chargement terminé');
 }
 };

// Fonction pour renommer une session (inline)
const handleRenameSession = async (sessionId: string, newTitle: string) => {
if (!user?.id) {
logger.error('handleRenameSession: user.id manquant, ABORT');
return;
}

if (!newTitle.trim()) {
alert('Le titre ne peut pas être vide');
return;
}

try {
// SÉCURITÉ: JWT automatique via authFetch
const response = await authFetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
method: 'PATCH',
headers: {
'Content-Type': 'application/json'
},
body: JSON.stringify({ title: newTitle.trim() })
        });
        
        if (response.ok) {
const { session } = await response.json();
setChatHistory(prev => prev.map(s => 
s.id === sessionId ? { ...s, title: session.title } : s
));

// Quitter le mode édition
setEditingSessionId(null);
setEditingTitle('');

logger.log('[Session] Renommée:', sessionId);
} else {
const error = await response.json();
throw new Error(error.error || 'Erreur lors du renommage');
}
} catch (error: any) {
logger.error('[Session] Erreur renommage:', error);
alert(`Erreur: ${error.message || 'Impossible de renommer la session'}`);
}
};

// Fonction pour supprimer une session (sans modal - gestion inline)
const handleDeleteSession = async (sessionId: string, sessionTitle: string) => {
if (!user?.id) {
logger.error('handleDeleteSession: user.id manquant, ABORT');
return;
}

try {
// SÉCURITÉ: JWT automatique via authFetch
const response = await authFetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          // Retirer de la liste locale
          setChatHistory(prev => prev.filter(session => session.id !== sessionId));
          
          // Si c'est la session active, créer une nouvelle éphémère
          if (currentSessionId === sessionId) {
            const newSessionId = `ephemeral-${Date.now()}`;
            setMessages([]);
            setCurrentSessionId(newSessionId);
            setIsEphemeral(true);
          }
          
logger.log('[Session] Supprimée:', sessionId);
} else {
const error = await response.json();
throw new Error(error.error || 'Erreur lors de la suppression');
}
} catch (error: any) {
logger.error('[Session] Erreur suppression:', error);
alert(`Erreur: ${error.message || 'Impossible de supprimer la session'}`);
    }
  };

  // Créer ou récupérer session de chat
  const initializeSession = async () => {
    if (currentSessionId) return currentSessionId;
    return await createNewSession();
  };

  // NOUVELLE FONCTION: Créer une nouvelle session (force)
  const createNewSession = async () => {
    try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/chat/session`, {
        method: 'POST',
          body: JSON.stringify({
 title: 'Nouvelle analyse', // Titre par défaut, sera remplacé par l'IA au 1er message
            userId: user?.id
          })
      });

      if (response.ok) {
        const data = await response.json();
        const sessionId = data.sessionId || data.id;
        if (sessionId) {
          setCurrentSessionId(sessionId);
          if (onSessionCreated) onSessionCreated(sessionId);
          return sessionId;
        } else {
 logger.error('SessionId manquant dans la réponse:', data);
        }
      }
    } catch (error) {
 logger.error('Erreur création session:', error);
    }
    
    return null;
  };

  // Sauvegarder message dans Supabase (sauf si éphémère)
  const saveMessage = async (sessionId: string, message: Message) => {
    if (isEphemeral || sessionId.startsWith('ephemeral-')) return;
 if (message.role === 'system') return; // Ne pas sauvegarder les messages système (UI only)
    
    try {
 // SÉCURITÉ: JWT automatique via authFetch
 const response = await authFetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        body: JSON.stringify({
 user_id: user?.id, // FIX: Ajouter user_id pour rate limiting
          sessionId,
          role: message.role,
          content: message.content,
          thinking: message.thinking || null,
 metadata: message.metadata || {}, // Sauvegarder le metadata (attachedFile)
 model: quickMode ? 'claude-haiku-4-5-20251001' : 
                 thinkingMode ? 'claude-sonnet-4-20250514' : 'claude-sonnet-4-20250514'
        })
      });
 
 
 if (!response.ok) {
 const errorText = await response.text();
 logger.error('[DEBUG] saveMessage - Error response:', errorText);
 throw new Error(`HTTP ${response.status}: ${errorText}`);
 }
 
 const result = await response.json();
    } catch (error) {
 logger.error('Erreur sauvegarde message:', error);
    }
  };

  // Convertir conversation éphémère en permanente
  const convertEphemeralToPermanent = async () => {
    if (!isEphemeral || messages.length === 0) return;
    
    try {
      // Créer une vraie session
      const realSessionId = await initializeSession();
      
      // Sauvegarder tous les messages
      for (const message of messages) {
        await saveMessage(realSessionId, message);
      }
      
      // Passer en mode permanent
      setCurrentSessionId(realSessionId);
      setIsEphemeral(false);
      
      // Recharger l'historique pour voir la nouvelle conversation
      await loadChatHistory();
      
      
      if (onSessionCreated) onSessionCreated(realSessionId);
    } catch (error) {
 logger.error('Erreur conversion éphémère:', error);
    }
  };

  // Charger historique de chat pour une session
  const loadSessionHistory = async (sessionId: string) => {
 // PROTECTION: Ne pas recharger pendant une édition
 if (isEditingRef.current) {
 logger.warn('[PROTECTION] loadSessionHistory ignoré (édition en cours)');
 return;
 }
 
 // SÉCURITÉ CRITIQUE: Vérifier authentification AVANT toute requête
 if (!user?.id) {
 logger.error('[SÉCURITÉ] loadSessionHistory: user.id manquant - BLOQUÉ', { sessionId });
 setMessages([]); // Vider les messages si pas authentifié
 return;
 }
 
 const CACHE_KEY = `clairify_chat_${sessionId}`;
 const CACHE_TTL = 60 * 1000; // 1 minute
 
 // OPTIMISATION 1: Charger le cache INSTANTANÉMENT
 try {
 const cached = localStorage.getItem(CACHE_KEY);
 if (cached) {
 const { data, timestamp } = JSON.parse(cached);
 const age = Date.now() - timestamp;
 
 if (age < CACHE_TTL) {
 logger.log(`[Chat] Cache instantané (${Math.round(age/1000)}s)`);
 setMessages(data);
 
 // Si cache < 20s, skip l'API complètement
 if (age < 20000) {
 logger.log(`[Chat] Cache récent, skip API`);
 return;
 }
 
 // Sinon, refresh en background silencieusement
 logger.log(`[Chat] Refresh background...`);
 setTimeout(() => {
 authFetch(`${API_URL}/api/chat/history/${sessionId}`)
 .then(res => res.ok ? res.json() : null)
 .then(data => {
 if (data && currentSessionId === sessionId) {
 const formatted = data.messages.map((msg: any) => ({
 id: msg.id,
 role: msg.role,
 content: msg.content,
 thinking: msg.thinking_content,
 timestamp: new Date(msg.timestamp)
 }));
 setMessages(formatted);
 localStorage.setItem(CACHE_KEY, JSON.stringify({
 data: formatted,
 timestamp: Date.now()
 }));
 logger.log(`[Chat] Cache mis à jour`);
 }
 })
 .catch(err => logger.warn('Erreur refresh background:', err));
 }, 50);
 
 return; // Retourner immédiatement avec le cache
 }
 }
 } catch (cacheError) {
 logger.warn('Erreur lecture cache:', cacheError);
 }
 
 // Pas de cache valide, charger depuis l'API
 logger.log('[SÉCURITÉ] Chargement historique session avec JWT', { 
 sessionId, 
 userId: user.id,
 timestamp: new Date().toISOString()
 });
 
 try {
 const fetchStart = Date.now();
 const response = await authFetch(`${API_URL}/api/chat/history/${sessionId}`);
 
      if (response.ok) {
 const data = await response.json();
 const fetchTime = Date.now() - fetchStart;
 logger.log(`[Chat] Messages chargés en ${fetchTime}ms`);
 
 const { messages: historyMessages } = data;
 
        const formattedMessages = historyMessages.map((msg: any) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          thinking: msg.thinking_content,
 timestamp: new Date(msg.timestamp),
 metadata: msg.metadata || {} // Récupérer le metadata (attachedFile)
 }));
 
 // FIX: Merger au lieu d'écraser pour éviter de perdre les messages locaux
 setMessages(prev => {
 const historyIds = new Set(formattedMessages.map((m: Message) => m.id));
 const tempMessages = prev.filter((m: Message) => !historyIds.has(m.id));
 
 if (tempMessages.length > 0) {
 return [...formattedMessages, ...tempMessages];
 }
 
 return formattedMessages;
 });
 
 // Sauvegarder en cache
 try {
 localStorage.setItem(CACHE_KEY, JSON.stringify({
 data: formattedMessages,
 timestamp: Date.now()
 }));
 logger.log(`[Chat] Sauvegardé en cache (${formattedMessages.length} messages)`);
 } catch (cacheErr) {
 logger.warn('Erreur sauvegarde cache:', cacheErr);
 }
 
 } else {
 const errorText = await response.text();
 logger.error('[DEBUG] loadSessionHistory - Error response:', errorText);
      }
    } catch (error) {
 logger.error('Erreur chargement historique:', error);
    }
  };

 // FIX: Scroll intelligent - Ne pas scroller si user lit en haut
  useEffect(() => {
 if (!messagesEndRef.current) return;
 
 const container = messagesEndRef.current.parentElement;
 if (!container) return;
 
 // Détecter si user est proche du bas (dans les 150px)
 const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
 
 // Scroller UNIQUEMENT si user est déjà proche du bas OU si c'est un nouveau message user
 const lastMessage = messages[messages.length - 1];
 const isNewUserMessage = lastMessage?.role === 'user';
 
 if (isNearBottom || isNewUserMessage) {
 messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
 }
 }, [messages.length]); // Dépendre uniquement du nombre de messages, pas du contenu


 // Supprimé: Gérer les analyses en arrière-plan (causait des requêtes fantômes)

  // Charger historique au démarrage et quand sessionId change
  useEffect(() => {
 // DEBUG: Log quand useEffect se déclenche
 logger.log('[useEffect] Déclenchement:', { 
 providedSessionId, 
 userId: user?.id,
 triggerCount: Math.random().toString(36).substr(2, 5) // ID unique pour tracer
 });
 
    const loadInitialData = async () => {
 // SÉCURITÉ: Ne charger que si user authentifié
 if (!user?.id) {
 logger.warn('useEffect loadInitialData: En attente de user.id...');
 return;
 }
 
 // Charger l'historique UNE SEULE FOIS (pas de double appel)
 await loadChatHistory();
 
      if (providedSessionId) {
        setCurrentSessionId(providedSessionId);
        await loadSessionHistory(providedSessionId);
      } else {
 // Si pas de session fournie, charger la dernière depuis chatHistory
 // (déjà chargé par loadChatHistory ci-dessus)
 // On attendra que chatHistory soit mis à jour, puis on sélectionne la première
      }
    };

    loadInitialData();
 }, [providedSessionId, user?.id]); // Dépendance sur user.id
 
 // Sélectionner automatiquement la première session si aucune n'est active
 // PROTECTION: Ne se déclenche qu'UNE SEULE FOIS au montage initial
 const hasAutoSelectedRef = useRef(false);
 
 useEffect(() => {
 // Ne pas auto-sélectionner si:
 // 1. Déjà auto-sélectionné une fois
 // 2. Une session est déjà active
 // 3. Pas d'historique
 // 4. User non authentifié
 // 5. Un providedSessionId est fourni (on attend qu'il charge)
 if (
 hasAutoSelectedRef.current || 
 currentSessionId || 
 chatHistory.length === 0 || 
 !user?.id ||
 providedSessionId
 ) {
 return;
 }
 
 // Auto-sélectionner la dernière session
 const lastSession = chatHistory[0];
 logger.log('[AUTO-SELECT] Sélection automatique de la dernière session:', lastSession.id);
 setCurrentSessionId(lastSession.id);
 loadSessionHistory(lastSession.id);
 if (onSessionCreated) onSessionCreated(lastSession.id);
 
 // Marquer comme auto-sélectionné pour éviter de re-trigger
 hasAutoSelectedRef.current = true;
 }, [chatHistory, currentSessionId, user?.id, providedSessionId]);

 // Auto-resize textarea avec limite de hauteur
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
 const newHeight = Math.min(textareaRef.current.scrollHeight, 200); // Max 200px (environ 6-7 lignes)
 textareaRef.current.style.height = newHeight + 'px';
    }
  }, [inputValue]);

 // NOTE: Parsing des graphiques désactivé - géré par pdfExportV3.ts pour l'export PDF
 /*useEffect(() => {
    if (messages.length === 0) return;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role !== 'assistant' || !chartsMode) return;
    
    // AMÉLIORÉ: Détection plus flexible des graphiques avec patterns multiples
    const patterns = [
 // Pattern pour SPECIFICATION JSON: {...} (SANS backticks - format du prompt)
 /SPECIFICATION\s+JSON:\s*(\{[^{}]*\{[^{}]*\}[^{}]*\})/g,
      // Pattern principal pour ```json avec chart
      /```json\s*(\{[\s\S]*?"type":\s*"(bar|pie|line|chart)"[\s\S]*?\})\s*```/g,
      // Pattern pour JSON sans backticks (cas des réponses longues)
      /\{\s*"type":\s*"(bar|pie|line|chart)"[\s\S]*?"data":\s*\[[\s\S]*?\][\s\S]*?\}/g,
      // Pattern alternatif pour ```chart
      /```chart\s*(\{[\s\S]*?\})\s*```/g,
      // Pattern pour blocks de données structurées
      /```data\s*(\{[\s\S]*?"type":\s*"(bar|pie|line)"[\s\S]*?\})\s*```/g,
      // Pattern pour graphiques en fin de message
      /(?:```json\s*)?(\{[\s\S]*?"type":\s*"(bar|pie|line|chart)"[\s\S]*?"data":\s*\[[\s\S]*?\][\s\S]*?\})(?:\s*```)?/g
    ];
    
    let allCharts: any[] = [];
    
    for (const pattern of patterns) {
      const matches = Array.from(lastMessage.content.matchAll(pattern));
      
      for (const match of matches) {
        try {
          let jsonContent = match[1];
          
          // Nettoyer le JSON si nécessaire
          jsonContent = jsonContent.trim();
          
          const parsedChart = JSON.parse(jsonContent);
          
          // Vérifier que c'est bien un graphique
          if (parsedChart.type && ['bar', 'pie', 'line', 'chart'].includes(parsedChart.type)) {
            // Générer des données d'exemple si manquantes
            if (!parsedChart.data || parsedChart.data.length === 0) {
              if (parsedChart.type === 'pie') {
                parsedChart.data = [
                  { label: 'Segment A', value: 35 },
                  { label: 'Segment B', value: 25 },
                  { label: 'Segment C', value: 20 },
                  { label: 'Segment D', value: 20 }
                ];
              } else if (parsedChart.type === 'bar' || parsedChart.type === 'chart') {
                parsedChart.data = [
                  { label: 'Q1', value: 120 },
                  { label: 'Q2', value: 180 },
                  { label: 'Q3', value: 220 },
                  { label: 'Q4', value: 190 }
                ];
              }
            }
            
            // Titre par défaut si manquant
            if (!parsedChart.title) {
              parsedChart.title = parsedChart.type === 'pie' ? 'Répartition' : 'Évolution';
            }
            
            allCharts.push({ 
              id: `chart-${Date.now()}-${allCharts.length}`, 
              ...parsedChart 
            });
          }
        } catch (e) {
 logger.warn('Erreur parsing chart:', e, 'Content:', match[1]);
        }
      }
    }
    
    if (allCharts.length > 0) {
      setDetectedCharts(allCharts);
    }
 }, [messages, chartsMode]);*/

 // SÉCURITÉ: Gestion upload de fichiers avec validation robuste exhaustive
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

 // SÉCURITÉ: Vérifier authentification
 if (!user) {
 const errorMsg: Message = {
 id: Date.now().toString(),
 role: 'system',
 content: 'Vous devez être connecté pour uploader un fichier',
 timestamp: new Date(),
 metadata: { isError: true }
 };
 setMessages(prev => [...prev, errorMsg]);
      return;
    }

 // SÉCURITÉ: Bloquer upload pendant streaming
 if (isLoading || isSendingRef.current) {
 const errorMsg: Message = {
 id: Date.now().toString(),
 role: 'system',
 content: 'Attendez la fin de la réponse en cours avant d\'uploader un fichier',
 timestamp: new Date(),
 metadata: { isError: true }
 };
 setMessages(prev => [...prev, errorMsg]);
 return;
 }

 try {
 // Importer l'utilitaire de validation
 const { validateFile } = await import('../utils/fileValidation');
 
 // Valider le fichier de manière sécurisée
 const validation = await validateFile(file);
 
 if (!validation.valid) {
 const errorMsg: Message = {
 id: Date.now().toString(),
 role: 'system',
 content: validation.error || 'Fichier invalide',
 timestamp: new Date(),
 metadata: { isError: true }
 };
 setMessages(prev => [...prev, errorMsg]);
 // Reset input pour permettre re-upload du même fichier
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 return;
 }

 // Afficher un loading
    setUploadedFile({
      name: file.name,
      type: file.type,
      size: file.size,
 status: 'uploading',
 fullContent: '' // Sera rempli après upload
 });

 // NOUVEAU : Upload vers Scaleway Object Storage avec processing
 const { uploadFileWithProcessing } = await import('../lib/storage');
 
 const { fileKey, fileId, orgPath, dataPreview } = await uploadFileWithProcessing(
 file,
 user.id,
 currentSessionId || undefined
 );

 logger.log(`[FluidChat] Fichier uploadé - Key: ${fileKey}, ID: ${fileId}, Org: ${orgPath}`);

 // Parser le fichier de manière robuste (CSV, Excel, JSON)
 const { parseFileForAnalysis, formatDataForAI } = await import('../utils/fileParser');
 
 logger.log(`[FluidChat] Parsing fichier: ${file.name}`);
 const parsedData = await parseFileForAnalysis(file);
 
 if (!parsedData.success) {
   const errorMsg: Message = {
     id: Date.now().toString(),
     role: 'system',
     content: `Erreur parsing fichier: ${parsedData.error}`,
     timestamp: new Date(),
     metadata: { isError: true }
   };
   setMessages(prev => [...prev, errorMsg]);
   setUploadedFile(null);
   if (fileInputRef.current) {
     fileInputRef.current.value = '';
   }
   return;
 }
 
 // Formater les données pour l'analyse IA
 const content = formatDataForAI(parsedData);
 
 logger.log(`[FluidChat] Fichier parsé: ${parsedData.rowCount} lignes, ${parsedData.columnCount} colonnes`);
 logger.log(`[FluidChat] Contenu formaté généré: ${content.length} caractères`, {
 preview: content.substring(0, 300)
 });
 
 // Vérifier que le contenu est valide
 if (!content || content.trim().length === 0) {
 logger.error('[FluidChat] ⚠️ ERREUR: formatDataForAI a généré un contenu vide!', {
 parsedDataSuccess: parsedData.success,
 parsedDataRowCount: parsedData.rowCount,
 parsedDataColumnCount: parsedData.columnCount,
 hasData: !!parsedData.data,
 dataLength: parsedData.data?.length || 0
 });
 }
 
 // Stocker le fichier avec toutes les infos
 setUploadedFile({
 name: file.name,
 type: file.type,
 size: file.size,
 status: 'uploaded',
 fullContent: content, // Contenu formaté pour l'IA
 parsedData: parsedData, // Données brutes parsées
 fileKey: fileKey,
 fileId: fileId,
 orgPath: orgPath,
 dataPreview: dataPreview // Aperçu des données
 });

 } catch (error) {
 logger.error('[FluidChat] Erreur upload:', error);
 
 // UX: Message d'erreur contextuel et élégant
 let errorContent = 'Erreur lors de l\'upload du fichier';
 let canRetry = true;
 
 if (error instanceof Error) {
 const msg = error.message.toLowerCase();
 
 if (msg.includes('tokens insuffisants') || msg.includes('insufficient')) {
 errorContent = 'Crédits insuffisants pour uploader ce fichier';
 canRetry = false;
 setRequiredTokensForRequest(5000); // Estimation
 setShowInsufficientTokensModal(true);
 } else if (msg.includes('network') || msg.includes('fetch')) {
 errorContent = 'Erreur réseau. Vérifiez votre connexion';
 } else if (msg.includes('too large') || msg.includes('size')) {
 errorContent = 'Fichier trop volumineux (max 200 MB)';
 canRetry = false;
 } else if (msg.includes('unauthorized') || msg.includes('401')) {
 errorContent = 'Session expirée. Reconnectez-vous';
 canRetry = false;
 } else if (msg.includes('forbidden') || msg.includes('403')) {
 errorContent = 'Accès refusé. Vérifiez vos permissions';
 canRetry = false;
 } else {
 errorContent = `Erreur: ${error.message}`;
 }
 }
 
 const errorMsg: Message = {
 id: Date.now().toString(),
 role: 'system',
 content: errorContent,
 timestamp: new Date(),
 metadata: { 
 isError: true,
 canRetry: canRetry
 }
 };
 setMessages(prev => [...prev, errorMsg]);
 
 // Reset l'état
 setUploadedFile(null);
 if (fileInputRef.current) {
 fileInputRef.current.value = '';
 }
 }
 };

 // ============================================================
 // AGENT MODE: Handler pour le système d'agents autonomes
 // ============================================================
 const handleAgentMode = async (userMessage: Message, sessionId: string, analysisId: string, fileData?: any) => {
 // RÉINITIALISER les states Agent Mode
 setAgentSteps([]);
 setCurrentThinking('');
 setNeedsConfirmation(false);
 setConfirmationMessage('');
 setAgentPaused(false);
 
 
 // Variables locales pour le streaming
 let assistantMessageId = '';
 let finalReportContent = '';
 
 // DEBUG: Logger l'état du fileData reçu
 logger.log('[Agent] handleAgentMode appelé avec fileData:', {
 hasFileData: !!fileData,
 fileDataKeys: fileData ? Object.keys(fileData) : [],
 fileDataName: fileData?.name,
 hasFullContent: !!(fileData?.fullContent),
 hasParsedData: !!(fileData?.parsedData),
 hasDataPreview: !!(fileData?.dataPreview)
 });
 
 // PARSER le fichier uploadé OU extraire données du message
 let parsedData: string | null = null;
 
 // 1. Fichier uploadé (CSV, JSON, Excel) - PRIORITÉ ABSOLUE
 if (fileData) {
 // Utiliser fullContent si disponible (données formatées pour IA)
 if (fileData.fullContent && typeof fileData.fullContent === 'string' && fileData.fullContent.trim().length > 0) {
 parsedData = fileData.fullContent;
 logger.log('[Agent] ✅ Utilisation fullContent du fichier:', {
 fileName: fileData.name,
 contentLength: fileData.fullContent.length,
 preview: fileData.fullContent.substring(0, 200)
 });
 }
 // Sinon utiliser parsedData si disponible (formater pour IA)
 else if (fileData.parsedData) {
 if (fileData.parsedData.success && fileData.parsedData.data && Array.isArray(fileData.parsedData.data) && fileData.parsedData.data.length > 0) {
 // Formater les données parsées pour l'IA
 const { formatDataForAI } = await import('../utils/fileParser');
 parsedData = formatDataForAI(fileData.parsedData);
 logger.log('[Agent] ✅ Utilisation parsedData formaté du fichier:', {
 fileName: fileData.name,
 rowCount: fileData.parsedData.rowCount,
 columnCount: fileData.parsedData.columnCount,
 formattedLength: parsedData.length
 });
 } else {
 logger.warn('[Agent] parsedData existe mais invalide:', {
 success: fileData.parsedData.success,
 hasData: !!fileData.parsedData.data,
 dataLength: fileData.parsedData.data?.length || 0
 });
 }
 }
 // Sinon utiliser dataPreview si disponible
 if (!parsedData && fileData.dataPreview && typeof fileData.dataPreview === 'string' && fileData.dataPreview.trim().length > 0) {
 parsedData = fileData.dataPreview;
 logger.log('[Agent] ✅ Utilisation dataPreview du fichier:', {
 fileName: fileData.name,
 previewLength: fileData.dataPreview.length
 });
 }
 
 // DERNIER RECOURS: Si on a un fileKey mais pas de données, essayer de récupérer depuis le storage
 if (!parsedData && fileData.fileKey) {
 logger.warn('[Agent] ⚠️ Fichier uploadé mais aucune donnée disponible, fileKey:', fileData.fileKey);
 // On pourrait ici faire un fetch pour récupérer le fichier, mais c'est complexe
 // Pour l'instant, on log juste l'avertissement
 }
 }
 
 // 2. Données collées dans l'input (JSON, CSV brut, tableau) - FALLBACK
 if (!parsedData && userMessage.content.length > 200) {
 // Si le message est long, il contient peut-être des données
 const content = userMessage.content;
 
 // Détecter JSON
 if (content.includes('{') && content.includes('}') || content.includes('[') && content.includes(']')) {
 try {
 JSON.parse(content);
 parsedData = content;
 logger.log('[Agent] Données JSON détectées dans le message');
 } catch (e) {
 // Pas du JSON pur, peut-être CSV ou texte structuré
 parsedData = content;
 logger.log('[Agent] Données texte structuré détectées dans le message');
 }
 }
 // Détecter CSV (virgules ou tabulations)
 else if (content.includes(',') || content.includes('\t')) {
 parsedData = content;
 logger.log('[Agent] Données CSV détectées dans le message');
 }
 }
 
 // DEBUG: Logger détaillé pour diagnostiquer
 logger.log('[Agent] État des données avant envoi:', {
 hasFileData: !!fileData,
 fileDataName: fileData?.name,
 hasFullContent: !!(fileData?.fullContent && typeof fileData.fullContent === 'string' && fileData.fullContent.trim().length > 0),
 fullContentLength: fileData?.fullContent?.length || 0,
 hasParsedData: !!(fileData?.parsedData && fileData.parsedData.success),
 parsedDataRows: fileData?.parsedData?.rowCount || 0,
 hasDataPreview: !!(fileData?.dataPreview && typeof fileData.dataPreview === 'string' && fileData.dataPreview.trim().length > 0),
 dataPreviewLength: fileData?.dataPreview?.length || 0,
 parsedDataFinal: !!parsedData,
 parsedDataLength: parsedData?.length || 0,
 parsedDataPreview: parsedData ? parsedData.substring(0, 300) : null
 });
 
 // Si aucune donnée n'a été trouvée, logger un avertissement détaillé
 if (!parsedData) {
 logger.warn('[Agent] ⚠️ AUCUNE DONNÉE TROUVÉE pour l\'analyse', {
 hasFileData: !!fileData,
 fileDataKeys: fileData ? Object.keys(fileData) : [],
 fileDataValues: fileData ? {
 hasFullContent: !!fileData.fullContent,
 fullContentType: typeof fileData.fullContent,
 hasParsedData: !!fileData.parsedData,
 parsedDataType: typeof fileData.parsedData,
 hasDataPreview: !!fileData.dataPreview,
 dataPreviewType: typeof fileData.dataPreview
 } : null,
 messageLength: userMessage.content.length
 });
 }
 
 try {
 // Créer un nouvel AbortController pour cette requête
 abortControllerRef.current = new AbortController();
 
 // SÉCURITÉ: JWT automatique pour streaming (via getAuthHeaders car SSE nécessite headers personnalisés)
 const authHeaders = await getAuthHeaders();
 logger.log('[SÉCURITÉ] Streaming Agent avec JWT', { 
 userId: user?.id, 
 sessionId,
 hasUploadedData: !!parsedData,
 uploadedDataSize: parsedData?.length || 0
 });
 
 const response = await fetch(`${API_URL}/api/llm/agent/stream`, {
 method: 'POST',
 headers: {
 ...authHeaders,
 'Accept': 'text/event-stream',
 'Cache-Control': 'no-cache'
 },
 body: JSON.stringify({
 userQuery: userMessage.content,
 uploadedData: parsedData || null, // Envoyer null explicitement si pas de données
 userId: user?.id || null,
 sessionId: sessionId || null
 }),
 signal: abortControllerRef.current.signal // Ajouter le signal d'annulation
 });

 if (!response.ok) {
 const errorText = await response.text();
 throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
 }

 const reader = response.body?.getReader();
 const decoder = new TextDecoder();

 if (!reader) {
 throw new Error('No reader available');
 }

 let buffer = '';
 
 // Variable pour accumuler le rapport final
 let finalReportContent = '';
 
 // Parser le stream SSE
 while (true) {
 const { done, value } = await reader.read();
 if (done) break;

 buffer += decoder.decode(value, { stream: true });
 const lines = buffer.split('\n');
 buffer = lines.pop() || '';

 for (const line of lines) {
 if (line.startsWith('data: ')) {
 try {
 const data = JSON.parse(line.slice(6));
 

 // Initialisation des steps
 if (data.type === 'agent_init') {
 setAgentSteps(data.steps);
 }
 
 // Update d'un step
 else if (data.type === 'step_update') {
 if (data.step && data.step.id) {
 setAgentSteps(prev => 
 prev.map(step => 
 step.id === data.step.id ? { ...step, ...data.step } : step
 )
 );
 } else {
 logger.warn('step_update sans objet step complet:', data);
 }
 }
 
 // Contenu thinking - CUMULATIF
 else if (data.type === 'thinking_delta') {
 const newContent = data.content || '';
 setCurrentThinking(prev => {
 const updated = prev + newContent;
 return updated;
 });
 }
 
 // Confirmation nécessaire
 else if (data.type === 'needs_confirmation') {
 setNeedsConfirmation(true);
 setConfirmationMessage(data.message);
 setAgentPaused(true);
 }
 
 // Step complété
 else if (data.type === 'step_complete') {
 if (data.step && data.step.id) {
 setAgentSteps(prev => 
 prev.map(step => 
 step.id === data.step.id ? { ...step, status: 'completed', result: data.step.result } : step
 )
 );
 } else {
 logger.warn('step_complete sans objet step complet:', data);
 }
 }
 
 // Erreur - data.step peut être un string (stepId) ou un objet
 else if (data.type === 'agent_error') {
 logger.error(' [Agent] Erreur:', data.error);
 const stepId = typeof data.step === 'string' ? data.step : data.step?.id;
 if (stepId) {
 setAgentSteps(prev => 
 prev.map(step => 
 step.id === stepId ? { ...step, status: 'failed', error: data.error } : step
 )
 );
 } else {
 logger.warn('agent_error sans step ID:', data);
 }
 }
 
 // Rapport final en streaming - AFFICHER EN TEMPS RÉEL
 else if (data.type === 'final_report_delta') {
 const newContent = data.content || '';
 finalReportContent += newContent;
 
 // CRÉER ou METTRE À JOUR le message en temps réel
 setMessages(prev => {
 const lastMsg = prev[prev.length - 1];
 
 // Si le message assistant existe déjà, le mettre à jour
 if (lastMsg && lastMsg.role === 'assistant' && lastMsg.id === assistantMessageId) {
 return prev.map((msg, idx) => 
 idx === prev.length - 1 
 ? { ...msg, content: finalReportContent }
 : msg
 );
 } else {
 // Sinon, créer le message la PREMIÈRE fois
 assistantMessageId = Date.now().toString();
 return [...prev, {
 id: assistantMessageId,
 role: 'assistant',
 content: finalReportContent,
 thinking: currentThinking, // Thinking déjà attaché
 timestamp: new Date()
 }];
 }
 });
 // Scroll automatique pendant le streaming
 setTimeout(() => scrollToBottom(true), 50);
 }
 
 // Analyse complète - FINALISER
 else if (data.type === 'agent_complete') {
 
 // Mettre à jour le message avec le thinking final
 setMessages(prev => prev.map((msg, idx) => 
 idx === prev.length - 1 && msg.id === assistantMessageId
 ? { ...msg, content: finalReportContent, thinking: currentThinking }
 : msg
 ));
 
 // Sauvegarder en DB
 if (sessionId && assistantMessageId) {
 const finalMessage = {
 id: assistantMessageId,
 role: 'assistant' as const,
 content: finalReportContent || 'Rapport non disponible',
 thinking: currentThinking,
 timestamp: new Date()
 };
 saveMessage(sessionId, finalMessage).catch(err => 
 logger.error('Erreur sauvegarde:', err)
 );
 
 // GÉNÉRER ET SAUVEGARDER LES INSIGHTS depuis le rapport
 generateAndSaveInsights(sessionId, finalReportContent || '', user?.id, assistantMessageId)
 .then(insights => {
 if (insights && insights.length > 0) {
 // MISE À JOUR OPTIMISTE : Mettre à jour localement immédiatement
 setChatHistory(prev => prev.map(session => 
 session.id === sessionId 
 ? { ...session, insights_count: (session.insights_count || 0) + insights.length }
 : session
 ));
 
 // Recharger la liste des sessions depuis l'API pour synchroniser
 // SÉCURITÉ: Utiliser authFetch pour JWT
 if (user?.id) {
 setTimeout(async () => {
 try {
 const sessionsResponse = await authFetch(`${API_URL}/api/sessions/list/${user.id}`);
 if (sessionsResponse.ok) {
 const sessions = await sessionsResponse.json();
 setChatHistory(sessions);
 logger.log('Sessions rechargées après génération insights (agent mode)');
 }
 } catch (err) {
 logger.warn('Erreur rechargement sessions:', err);
 }
 }, 1000);
 }
 }
 })
 .catch(err => logger.warn('Erreur génération insights:', err));
 }
 
 // Reset UNIQUEMENT isLoading et agentMode
 setIsLoading(false);
 setAgentMode(false);
 setNeedsConfirmation(false);
 
 // Rafraîchir tokens
 try {
 refreshTokens();
 } catch (err) {
 logger.error('Erreur refresh tokens:', err instanceof Error ? err.message : String(err));
 }
 }
 
      } catch (error) {
 logger.error(' [Agent] Erreur parsing:', error);
 }
 }
 }
 }
 
 } catch (error: any) {
 // Si la requête a été annulée par l'utilisateur, ne pas afficher d'erreur
 if (error.name === 'AbortError') {
 return;
 }
 
 logger.error(' [Agent] Erreur:', error);
 setIsLoading(false);
 alert(`Erreur Agent Mode: ${error.message}`);
 }
 };

 // Handlers pour les actions Agent
 const handleAgentContinue = () => {
 setAgentPaused(false);
 setNeedsConfirmation(false);
 // TODO: Envoyer signal au backend pour continuer
 };

 const handleAgentPause = () => {
 setAgentPaused(true);
 // TODO: Envoyer signal au backend pour pause
 };

 const handleAgentProvideInput = (input: string) => {
 // TODO: Envoyer input au backend
 };

 const handleSend = async () => {
 // LOG DEBUG: Tracer tous les appels à handleSend
 const stackTrace = new Error().stack?.split('\n').slice(1, 4).join('\n') || 'unknown';
 logger.log('[HANDLE_SEND] Appelé depuis:', stackTrace);
 
 // DÉBLOQUER rechargement (édition terminée)
 isEditingRef.current = false;
 
 // PROTECTION ANTI-DOUBLE-ENVOI : Éviter spam de clics
 if (isSendingRef.current) {
 logger.warn('Envoi déjà en cours, requête ignorée');
 return;
 }

 // Validation : Soit un message non-vide, soit un fichier uploadé
 const userText = inputValue.trim();
 if (!userText && !uploadedFile) {
 logger.warn('Aucun contenu à envoyer');
 return;
 }

 // SÉCURITÉ : Vérifier authentification
 if (!user?.id) {
 const errorMsg: Message = {
 id: Date.now().toString(),
 role: 'system',
 content: 'Vous devez être connecté pour envoyer un message',
 timestamp: new Date(),
 metadata: { isError: true }
 };
 setMessages(prev => [...prev, errorMsg]);
 return;
 }

 // VÉRIFICATION TOKENS : Estimation coût de la requête
 const estimatedTokens = Math.ceil(Math.max(userText.length, uploadedFile?.size || 0) * 1.5);
 const requiredTokens = Math.max(estimatedTokens, 1000);

 if (!hasEnoughTokens(requiredTokens)) {
 logger.warn('[Tokens] Insuffisants:', balance, 'requis:', requiredTokens);
 setRequiredTokensForRequest(requiredTokens);
 setShowInsufficientTokensModal(true);
 return;
 }

 // Créer le contenu du message (texte uniquement, fichier dans metadata)
 const messageContent = userText || 'Analysez ce fichier';
 
 let sessionId = currentSessionId;
 
 // GESTION SESSIONS SUPABASE
 if (!sessionId) {
 
 // Créer une nouvelle session Supabase automatiquement
 sessionId = await initializeSession();
 if (!sessionId) {
 logger.error('Impossible de créer session');
 return;
 }
 setCurrentSessionId(sessionId);
 setIsEphemeral(false);
 
 // Recharger la liste des conversations pour afficher la nouvelle session
 loadChatHistory().catch(err => logger.warn('Erreur rechargement historique:', err));
 }
 
 // Variable pour stocker le titre généré par l'IA (sera mis à jour via le stream)
 let generatedTitle: string | null = null;
 const isFirstMessage = messages.length === 0;

 // SAUVEGARDER le fichier AVANT de le nettoyer
 const fileToAnalyze = uploadedFile;
 
 // VÉRIFIER si le fichier est encore en cours de parsing
 if (fileToAnalyze && fileToAnalyze.status === 'uploading') {
 logger.warn('[Chat] ⚠️ Fichier encore en cours de parsing, attente...');
 // Attendre que le parsing soit terminé (max 10 secondes)
 let waitCount = 0;
 while (fileToAnalyze.status === 'uploading' && waitCount < 100) {
 await new Promise(resolve => setTimeout(resolve, 100));
 waitCount++;
 // Vérifier à nouveau l'état depuis uploadedFile (qui peut avoir changé)
 if (uploadedFile && uploadedFile.status === 'uploaded') {
 // Mettre à jour fileToAnalyze avec les nouvelles données
 Object.assign(fileToAnalyze, uploadedFile);
 break;
 }
 }
 if (fileToAnalyze.status === 'uploading') {
 logger.error('[Chat] ⚠️ Timeout: le fichier n\'a pas fini de parser après 10 secondes');
 const errorMsg: Message = {
 id: Date.now().toString(),
 role: 'system',
 content: 'Le fichier est encore en cours de traitement. Veuillez patienter quelques secondes et réessayer.',
 timestamp: new Date(),
 metadata: { isError: true }
 };
 setMessages(prev => [...prev, errorMsg]);
 return;
 }
 }
 
 // DEBUG: Logger l'état du fichier
 if (fileToAnalyze) {
 logger.log('[Chat] 📋 État du fichier au moment de l\'envoi:', {
 fileName: fileToAnalyze.name,
 status: fileToAnalyze.status,
 hasFullContent: !!(fileToAnalyze.fullContent && typeof fileToAnalyze.fullContent === 'string'),
 fullContentLength: fileToAnalyze.fullContent?.length || 0,
 hasParsedData: !!(fileToAnalyze.parsedData && fileToAnalyze.parsedData.success),
 hasDataPreview: !!(fileToAnalyze.dataPreview && typeof fileToAnalyze.dataPreview === 'string'),
 allKeys: Object.keys(fileToAnalyze)
 });
 } else {
 logger.warn('[Chat] ⚠️ Aucun fichier disponible au moment de l\'envoi');
 }

 // Créer le message utilisateur avec métadonnées du fichier
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
 content: messageContent,
 timestamp: new Date(),
 metadata: uploadedFile ? {
 attachedFile: {
 name: uploadedFile.name,
 size: uploadedFile.size,
 type: uploadedFile.type,
 fileKey: uploadedFile.fileKey,
 fileId: uploadedFile.fileId,
 orgPath: uploadedFile.orgPath
 }
 } : undefined
 };

 // Marquer comme en cours d'envoi
 isSendingRef.current = true;

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
 
 // ATTENTION: Ne PAS nettoyer le fichier ici car on en a besoin pour fileToAnalyze
 // Le fichier sera nettoyé après l'envoi réussi
 // setUploadedFile(null); // DÉSACTIVÉ - on garde le fichier pour fileToAnalyze
 if (fileInputRef.current) {
   fileInputRef.current.value = '';
 }
    setIsLoading(true);
    setCurrentThinking('');
 streamingMessageIdRef.current = null; // Reset streaming ref


 // Génération de l'ID de message
 const messageId = `msg-${Date.now()}`;

    // Sauvegarder message utilisateur
    await saveMessage(sessionId, userMessage);

    try {
 // ========== THINKING MODE AVEC AGENTS INTELLIGENTS ==========
 // Les agents s'activent automatiquement si:
 // - Thinking mode activé
 // - ET (fichier uploadé OU requête complexe > 100 caractères)
 const shouldUseAgents = thinkingMode && (fileToAnalyze || messageContent.length > 100);
 
 if (shouldUseAgents) {
 setAgentMode(true); // Activer visuellement
 await handleAgentMode(userMessage, sessionId, messageId, fileToAnalyze);
 return;
 }
 
 // ========== MODE STANDARD: Chat streaming ==========
 // Créer un AbortController pour pouvoir annuler la requête
 abortControllerRef.current = new AbortController();
 
 // Préparer le contenu avec fichier si présent
 let finalContent = userMessage.content;
 if (fileToAnalyze) {
 logger.log('[Chat] 🔍 Vérification données fichier:', {
 fileName: fileToAnalyze.name,
 hasFullContent: !!(fileToAnalyze.fullContent && typeof fileToAnalyze.fullContent === 'string' && fileToAnalyze.fullContent.trim().length > 0),
 fullContentLength: fileToAnalyze.fullContent?.length || 0,
 hasParsedData: !!(fileToAnalyze.parsedData && fileToAnalyze.parsedData.success),
 hasDataPreview: !!(fileToAnalyze.dataPreview && typeof fileToAnalyze.dataPreview === 'string' && fileToAnalyze.dataPreview.trim().length > 0)
 });
 
 // Utiliser fullContent si disponible (données formatées pour IA)
 if (fileToAnalyze.fullContent && typeof fileToAnalyze.fullContent === 'string' && fileToAnalyze.fullContent.trim().length > 0) {
 finalContent = `${userMessage.content}\n\n📊 DONNÉES DU FICHIER ${fileToAnalyze.name}:\n\n${fileToAnalyze.fullContent}`;
 logger.log('[Chat] ✅ Utilisation fullContent du fichier:', {
 fileName: fileToAnalyze.name,
 contentLength: fileToAnalyze.fullContent.length,
 finalContentLength: finalContent.length,
 preview: finalContent.substring(0, 500)
 });
 }
 // Sinon formater les données parsées
 else if (fileToAnalyze.parsedData && fileToAnalyze.parsedData.success && fileToAnalyze.parsedData.data) {
 const { formatDataForAI } = await import('../utils/fileParser');
 const formattedData = formatDataForAI(fileToAnalyze.parsedData);
 finalContent = `${userMessage.content}\n\n📊 DONNÉES DU FICHIER ${fileToAnalyze.name}:\n\n${formattedData}`;
 logger.log('[Chat] ✅ Formatage parsedData du fichier:', {
 fileName: fileToAnalyze.name,
 rowCount: fileToAnalyze.parsedData.rowCount,
 columnCount: fileToAnalyze.parsedData.columnCount,
 finalContentLength: finalContent.length
 });
 }
 // Sinon utiliser dataPreview (vérifier que c'est une chaîne)
 else if (fileToAnalyze.dataPreview && typeof fileToAnalyze.dataPreview === 'string' && fileToAnalyze.dataPreview.trim().length > 0) {
 finalContent = `${userMessage.content}\n\n📊 DONNÉES DU FICHIER ${fileToAnalyze.name}:\n\n${fileToAnalyze.dataPreview}`;
 logger.log('[Chat] ✅ Utilisation dataPreview du fichier:', {
 fileName: fileToAnalyze.name,
 finalContentLength: finalContent.length
 });
 } else {
 logger.error('[Chat] ⚠️ Fichier uploadé mais AUCUNE donnée disponible!', {
 fileName: fileToAnalyze.name,
 keys: Object.keys(fileToAnalyze)
 });
 }
 } else {
 logger.warn('[Chat] ⚠️ Aucun fichier à analyser (fileToAnalyze est null)');
 }
 
 // SÉCURITÉ: JWT automatique pour streaming (via getAuthHeaders car SSE nécessite headers personnalisés)
 const authHeaders = await getAuthHeaders();
 
 // DEBUG: Logger le contenu final envoyé
 logger.log('[Chat] 📤 Envoi au backend:', {
 finalContentLength: finalContent.length,
 hasFileData: finalContent.includes('📊 DONNÉES DU FICHIER') || finalContent.includes('Données du fichier'),
 messagesCount: messages.length + 1,
 finalContentPreview: finalContent.substring(0, 1000),
 userMessageLength: userMessage.content.length
 });
 
 logger.log('[SÉCURITÉ] Streaming Chat avec JWT', { 
 userId: user?.id, 
 sessionId,
 messagesCount: messages.length,
 finalContentHasData: finalContent.length > userMessage.content.length
 });
 
 const response = await fetch(`${API_URL}/api/llm/chat/stream`, {
        method: 'POST',
        headers: {
 ...authHeaders,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache'
        },
 signal: abortControllerRef.current.signal, // Ajouter le signal pour pouvoir annuler
        body: JSON.stringify({
          messages: [
            ...messages.map(m => ({
              role: m.role,
              content: m.content
            })),
            {
              role: userMessage.role,
 content: finalContent
            }
          ],
          thinkingMode: thinkingMode,
          webSearchEnabled: webSearchMode,
          chartsEnabled: chartsMode,
 quickMode: quickMode,
 userId: user?.id || null,
 sessionId: sessionId || null
        }),
      });
 

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No reader available');
      }

      let buffer = '';
      let isCompleted = false;
      
 // NOUVELLE LOGIQUE : Créer le message assistant UNE SEULE FOIS avec ref
 if (!streamingMessageIdRef.current) {
 const newMessageId = (Date.now() + 1).toString();
 streamingMessageIdRef.current = newMessageId;
 
 const assistantMessage: Message = {
 id: newMessageId,
      role: 'assistant',
      content: '',
      thinking: '',
      timestamp: new Date()
    };

 setMessages(prev => [...prev, assistantMessage]);
 }

 // Analyse sauvegardée directement dans Supabase via SSE stream

      while (true) {
        const { done, value } = await reader.read();
        if (done || isCompleted) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (isCompleted) break;
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
 
 // Log uniquement les événements importants (pas content_delta qui spam)
 if (data.type !== 'content_delta') {
 }
              
              if (data.type === 'content_delta') {
 // Mettre à jour le message en utilisant la ref
 if (streamingMessageIdRef.current) {
                setMessages(prev => prev.map(msg => 
 msg.id === streamingMessageIdRef.current 
                    ? { ...msg, content: data.fullContent || '' }
                    : msg
                ));
                // Scroll automatique pendant le streaming
                setTimeout(() => scrollToBottom(true), 50);
 }
 }
 
 // Gérer le titre de conversation généré par l'IA
 if (data.type === 'conversation_title' && data.title && isFirstMessage && sessionId) {
 generatedTitle = data.title;
 
 // Mettre à jour le titre dans Supabase
 // SÉCURITÉ: Utiliser authFetch pour JWT
 authFetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
 method: 'PATCH',
 body: JSON.stringify({ title: generatedTitle })
 }).catch(err => logger.warn('Erreur mise à jour titre:', err));
 
 // Mettre à jour localement
 setChatHistory(prev => 
 prev.map(session => 
 session.id === sessionId 
 ? { ...session, title: generatedTitle! }
 : session
 )
 );
 }
 
 // NOTE: Ce code est pour le mode thinking SANS agents (déprecié)
 // Le mode agent utilise handleAgentMode() à la place
 if (data.type === 'thinking_start') {
 setCurrentThinking('***Initialisation***\n\nDémarrage du processus de réflexion...');
              }
              
              if (data.type === 'thinking_delta' && thinkingMode) {
 const thinkingContent = data.fullThinking || data.thinking || '';
 setCurrentThinking(thinkingContent);
 if (streamingMessageIdRef.current) {
                setMessages(prev => 
                  prev.map(msg => 
 msg.id === streamingMessageIdRef.current 
 ? { ...msg, thinking: thinkingContent }
                      : msg
                  )
                );
              }
              }
              
              if (data.type === 'complete') {
                // Finaliser le message avec le contenu complet
 const finalContent = data.content || '';
 const finalThinking = data.thinking || '';
 
 if (streamingMessageIdRef.current) {
                setMessages(prev => 
                  prev.map(msg => 
 msg.id === streamingMessageIdRef.current 
 ? { ...msg, content: finalContent, thinking: finalThinking }
                      : msg
                  )
                );
                
                // Sauvegarder le message assistant final
 const finalMessage: Message = {
 id: streamingMessageIdRef.current,
 role: 'assistant',
 content: finalContent,
 thinking: finalThinking,
 timestamp: new Date()
 };
 await saveMessage(sessionId, finalMessage);
                
                // GÉNÉRATION ET SAUVEGARDE DES INSIGHTS
                try {
 const insights = await generateAndSaveInsights(sessionId, finalContent, user?.id, streamingMessageIdRef.current);
                  if (insights && insights.length > 0) {
 // MISE À JOUR OPTIMISTE : Mettre à jour localement immédiatement
 setChatHistory(prev => prev.map(session => 
 session.id === sessionId 
 ? { ...session, insights_count: (session.insights_count || 0) + insights.length }
 : session
 ));
 
 // Recharger la liste des sessions depuis l'API pour synchroniser
                    if (user?.id) {
 // Attendre un peu pour que le backend ait sauvegardé
 setTimeout(async () => {
 try {
 const sessionsResponse = await authFetch(`${API_URL}/api/sessions/list/${user.id}`);
                      if (sessionsResponse.ok) {
                        const sessions = await sessionsResponse.json();
                        setChatHistory(sessions);
 logger.log('Sessions rechargées après génération insights');
                      }
 } catch (err) {
 logger.warn('Erreur rechargement sessions:', err);
 }
 }, 1000);
                    }
                  }
                } catch (error) {
 logger.warn('Erreur génération insights:', error);
                }
                
                if (onAnalysisComplete) {
                  onAnalysisComplete({
                    ...data,
 sessionId: sessionId
                  });
                }
                }
                
 // Reset streaming ref et flag
 streamingMessageIdRef.current = null;
                isCompleted = true;
                break;
              }
              
              if (data.type === 'error') {
 logger.error('Stream error:', data.error);
 
 // UX: Message user-friendly avec retry au lieu d'erreur brute
 const errorMsg = data.error?.toLowerCase() || '';
 let userFriendlyError = 'Service temporairement indisponible';
 
 if (errorMsg.includes('timeout')) {
 userFriendlyError = 'Délai d\'attente dépassé';
 } else if (errorMsg.includes('model') || errorMsg.includes('anthropic')) {
 userFriendlyError = 'Modèle IA non disponible';
 } else if (errorMsg.includes('token') || errorMsg.includes('credit')) {
 userFriendlyError = 'Crédits insuffisants';
 } else if (errorMsg.includes('rate limit')) {
 userFriendlyError = 'Trop de requêtes, patientez';
 }
 
 // Message avec bouton retry (role system avec métadata)
 const errorMessage: Message = {
 id: (Date.now() + 3).toString(),
 role: 'system',
 content: userFriendlyError,
 timestamp: new Date(),
 metadata: { 
 isError: true, 
 canRetry: true,
 originalMessage: userMessage.content,
 originalFiles: fileToAnalyze ? [fileToAnalyze] : []
 }
 };
 
 setMessages(prev => [
 ...prev.filter(m => m.id !== streamingMessageIdRef.current), // Supprimer message vide assistant
 errorMessage
 ]);
 
 streamingMessageIdRef.current = null;
 isCompleted = true;
 break;
              }
            } catch (parseError) {
 logger.error('Error parsing SSE data:', parseError, 'Raw line:', line);
              // Continue la boucle même en cas d'erreur de parsing
              continue;
            }
          }
        }
      }
      
      // NETTOYER le fichier APRÈS l'envoi réussi (stream terminé)
      setUploadedFile(null);

    } catch (error) {
 // Si c'est une annulation utilisateur (AbortError), ne pas afficher d'erreur
 if (error instanceof Error && error.name === 'AbortError') {
 logger.log('⏹ Génération annulée par l\'utilisateur');
 return; // Exit silencieusement
 }
 
 // Log technique (console seulement)
 logger.error('Erreur:', error);
 
 // UX: Message contextuel, user-friendly et actionnable
 let errorContent = 'Une erreur est survenue';
 let canRetry = true;
 let isAuthError = false;
 
 if (error instanceof Error) {
 const msg = error.message.toLowerCase();
 
 if (msg.includes('network') || msg.includes('fetch') || msg.includes('cors') || msg.includes('failed to fetch')) {
 errorContent = 'Connexion perdue. Vérifiez votre réseau';
 canRetry = true;
 } else if (msg.includes('timeout')) {
 errorContent = 'Temps de réponse dépassé';
 canRetry = true;
 } else if (msg.includes('token') || msg.includes('credit') || msg.includes('insufficient')) {
 errorContent = 'Crédits insuffisants';
 canRetry = false;
 // Afficher modal tokens
 setRequiredTokensForRequest(requiredTokens || 1000);
 setShowInsufficientTokensModal(true);
 } else if (msg.includes('unauthorized') || msg.includes('401')) {
 errorContent = 'Session expirée. Reconnectez-vous';
 canRetry = false;
 isAuthError = true;
 } else if (msg.includes('forbidden') || msg.includes('403')) {
 errorContent = 'Accès refusé';
 canRetry = false;
 } else if (msg.includes('too large') || msg.includes('file size') || msg.includes('payload')) {
 errorContent = 'Fichier trop volumineux (max 200 MB)';
 canRetry = false;
 } else if (msg.includes('rate limit') || msg.includes('too many')) {
 errorContent = 'Trop de requêtes. Patientez quelques secondes';
 canRetry = true;
 } else if (msg.includes('model') || msg.includes('anthropic') || msg.includes('service')) {
 errorContent = 'Service IA temporairement indisponible';
 canRetry = true;
 } else {
 // Erreur générique mais informative
 errorContent = 'Erreur inattendue';
 canRetry = true;
 }
 }
 
 // Message système avec bouton retry si applicable
      const errorMessage: Message = {
        id: (Date.now() + 2).toString(),
 role: 'system',
 content: errorContent,
 timestamp: new Date(),
 metadata: {
 isError: true,
 canRetry: canRetry,
 originalMessage: inputValue || userMessage?.content,
 originalFiles: fileToAnalyze ? [fileToAnalyze] : []
 }
 };
 
 // Supprimer le message assistant vide si présent
 if (streamingMessageIdRef.current) {
 setMessages(prev => [
 ...prev.filter(m => m.id !== streamingMessageIdRef.current),
 errorMessage
 ]);
 } else {
      setMessages(prev => [...prev, errorMessage]);
 }
 
 // Si erreur d'auth, proposer reconnexion (optionnel, peut être géré par AuthProvider)
 if (isAuthError) {
 // TODO: Trigger auth refresh ou redirect login
 logger.warn('Session expirée, rafraîchissement nécessaire');
 }
    } finally {
      setIsLoading(false);
      setCurrentThinking('');
 isSendingRef.current = false; // Libérer le lock
 streamingMessageIdRef.current = null; // Reset streaming ref
 // NETTOYER le fichier dans finally pour être sûr qu'il est nettoyé même en cas d'erreur
 setUploadedFile(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
 logger.log('⌨ [ENTER KEY] Entrée appuyée (sans Shift)');
      handleSend();
    }
  };

  const extractInsightsFromContent = (content: string) => {
    const insights: any[] = [];
    
    // Rechercher les INSIGHTS ACTIONNABLES dans le texte
    const insightMatches = content.match(/\*\*INSIGHT ACTIONNABLE[^:]*:\*\*([^*]+)/g) || [];
    
    insightMatches.forEach((match, index) => {
      const insightText = match.replace(/\*\*INSIGHT ACTIONNABLE[^:]*:\*\*/, '').trim();
      
      // S'assurer que la description est assez longue (backend filtre < 50 caractères)
      if (insightText.length < 50) {
        logger.log('Insight trop court, ignoré:', insightText.substring(0, 30));
        return;
      }
      
      const firstSentence = insightText.split('.')[0] + '.';
      
      insights.push({
        id: `insight-${Date.now()}-${index}`,
        title: `Insight Stratégique ${index + 1}`,
        description: insightText,
        insight_type: 'recommendation',
        impact_level: 'high',
        chart_spec: null,
        supporting_metrics: {},
        actionable_recommendations: [firstSentence],
        created_at: new Date().toISOString()
      });
    });
    
    // Rechercher les métriques importantes avec contexte analytique
    const metriques = content.match(/[0-9,]+[€%]/g) || [];
    if (metriques.length > 0) {
      // Construire une description plus riche pour passer le filtre backend
      const description = `Analyse des métriques clés identifiées dans les données: ${metriques.slice(0, 5).join(', ')}. Ces indicateurs révèlent des tendances importantes et nécessitent une attention particulière pour optimiser les performances.`;
      
      insights.push({
        id: `metrics-${Date.now()}`,
        title: 'Métriques Clés Détectées',
        description: description, // Description enrichie pour passer le filtre backend
        insight_type: 'metric',
        impact_level: 'medium',
        chart_spec: null,
        supporting_metrics: { detected_metrics: metriques },
        actionable_recommendations: [`Analyser en détail les ${metriques.length} métriques identifiées pour identifier les opportunités d'optimisation`],
        created_at: new Date().toISOString()
      });
    }
    
    return insights;
  };

  const exportSingleMessage = async (content: string, filename: string) => {
    try {
 
 // Utiliser le nouveau système V3
 await downloadPDF(content, {
 title: 'Message Console',
 subtitle: filename,
 author: 'Clairify AI Analytics',
 timestamp: true
 }, filename.replace(/\s+/g, '_') + '.pdf');
 
    } catch (error) {
 logger.error('Erreur export message V3:', error);
    }
  };

  // GÉNÉRATION ET SAUVEGARDE DES INSIGHTS DEPUIS LE CONTENU IA
 const generateAndSaveInsights = async (sessionId: string, content: string, userId?: string, messageId?: string) => {
    if (!content || content.length < 100) {
 return [];
 }
 
 // PROTECTION ANTI-DOUBLE GENERATION basée sur hash du contenu (plus fiable)
 const contentHash = content.substring(0, 200); // Utiliser les 200 premiers caractères comme signature
 const trackingKey = messageId || `${sessionId}-${contentHash}`;
 if (insightsGeneratedForMessages.current.has(trackingKey)) {
      return [];
    }
    
    try {
 
 // Marquer comme en cours de génération
 insightsGeneratedForMessages.current.add(trackingKey);
      
      // Analyser le contenu pour extraire des insights pertinents
      const insights = extractInsightsFromContent(content);
      
      if (insights.length === 0) {
        logger.log('Aucun insight détecté dans le contenu');
        return [];
      }
      
      // Valider que les insights ont le format correct
      const validInsights = insights.filter(insight => 
        insight && 
        insight.title && 
        insight.description &&
        insight.insight_type
      );
      
      if (validInsights.length === 0) {
        logger.warn('Aucun insight valide après validation');
        return [];
      }
      
      // Sauvegarder dans Supabase via API
 // SÉCURITÉ: JWT automatique via authFetch
 // FORMAT BACKEND: Le backend attend 'type', 'impact', 'chartConfig', 'supportingData', 'userId'
 const response = await authFetch(`${API_URL}/api/insights/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: sessionId,
          insights: validInsights.map(insight => {
            // S'assurer que la description est assez longue (backend filtre < 50 caractères)
            let description = insight.description;
            if (description.length < 50) {
              // Enrichir la description pour passer le filtre backend
              description = `${description} Cette analyse révèle des tendances importantes nécessitant une attention particulière pour optimiser les performances et identifier les opportunités d'amélioration.`;
            }
            
            return {
              title: insight.title,
              description: description,
              type: insight.insight_type, // Backend attend 'type' pas 'insight_type'
              impact: insight.impact_level || 'medium', // Backend attend 'impact' pas 'impact_level'
              chartConfig: insight.chart_spec || null, // Backend attend 'chartConfig' pas 'chart_spec'
              supportingData: insight.supporting_metrics || {}, // Backend attend 'supportingData' pas 'supporting_metrics'
              actionable_recommendations: insight.actionable_recommendations || [],
              userId: userId, // Backend attend 'userId' pas 'user_id'
              messageId: messageId || null
            };
          })
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        logger.error('Erreur HTTP sauvegarde insight:', response.status, errorData);
        throw new Error(`Erreur sauvegarde insights: ${response.status} - ${errorData.error || 'Unknown'}`);
      }
      
      const result = await response.json();
      
      return insights;
      
    } catch (error) {
 logger.error('Erreur génération/sauvegarde insights:', error);
      return [];
    }
  };

  const exportPDF = async () => {
    try {
      
 // Récupérer tout le contenu des messages assistant
      const content = messages.filter(m => m.role === 'assistant').map(m => m.content).join('\n\n');
      const thinking = messages.filter(m => m.role === 'assistant' && m.thinking).map(m => m.thinking).join('\n\n');
      
 // Combiner contenu et thinking
 const fullContent = thinking ? `${content}\n\n## Analyse Détaillée\n\n${thinking}` : content;
 
 // Utiliser le nouveau système V3
 await downloadPDF(fullContent, {
 title: 'Conversation Console',
 subtitle: currentSessionId ? `Session ${currentSessionId.slice(0, 8)}` : 'Analyse IA',
 author: 'Clairify AI Analytics',
 timestamp: true
 }, 'clairefy-console.pdf');
 
    } catch (error) {
 logger.error('Erreur export PDF V3:', error);
      alert('Erreur lors de l\'export PDF');
    }
  };

  return (
 <div className={`flex h-full bg-theme-base ${className}`}>
 {/* Mobile Sidebar Overlay */}
 {mobileSidebarOpen && (
 <div 
 className="fixed inset-0 bg-theme-base/60 backdrop-blur-sm z-40 md:hidden"
 onClick={() => setMobileSidebarOpen(false)}
 />
 )}

 {/* Sidebar Premium - Adaptive light/dark - Hidden on mobile by default, overlay on mobile when open */}
 <div className={`
 fixed md:relative inset-y-0 left-0 z-50 md:z-auto
 w-72 bg-theme-surface text-theme-primary 
 flex-col border-r border-theme
 transition-transform duration-300
 ${mobileSidebarOpen ? 'translate-x-0 flex' : '-translate-x-full md:translate-x-0 md:flex'}
 `}>
 {/* Header minimaliste - Style Claude */}
 <div className="p-3 border-b border-theme-border">
          <button
 onClick={() => {
 logger.log('[NEW-CONVERSATION] Bouton cliqué');
 
 // Reset COMPLET de l'état
                 setMessages([]);
 setInputValue('');
 setUploadedFile(null);
 setCurrentSessionId('');
 // CORRECTION: Ne PAS mettre en éphémère par défaut
                 setIsEphemeral(false);
                 
 // Reset le flag auto-select pour permettre une future auto-sélection
 hasAutoSelectedRef.current = false;
 
 logger.log('[NEW-CONVERSATION] État reseté, nouvelle conversation normale (non-éphémère)');
 }}
 className="group w-full px-3 py-2 flex items-center justify-center space-x-2 text-sm text-theme-secondary hover:text-theme-primary bg-transparent hover:bg-theme-hover border border-theme"
 title="Nouvelle conversation"
 >
 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
 <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
 </svg>
 <span className="font-medium">Nouvelle conversation</span>
          </button>
        </div>

         {/* Liste des conversations - Interface ultra-moderne et sobre */}
 <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 custom-scrollbar">
           {historyLoading ? (
             <div className="flex items-center justify-center py-12">
               <div className="relative">
 <div className="w-6 h-6 border border-gray-300 dark:border-gray-600 border-t-purple-500 rounded-full animate-spin"></div>
 <div className="absolute inset-0 w-6 h-6 border border-transparent border-t-purple-400/50 rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>
               </div>
             </div>
 ) : (
 <>
 {/* NOUVELLE CONVERSATION EN COURS (pas encore sauvegardée) */}
 {!currentSessionId && messages.length === 0 && (
 <div className="mb-2 p-4 border border-theme bg-theme-hover">
 <div className="flex items-center space-x-2 mb-1">
 <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--spark-gold)' }}></div>
 <div className="text-sm font-medium text-theme-text-primary">
 <span>Nouvelle conversation</span>
 </div>
 </div>
 <div className="text-xs text-theme-text-secondary">
 Envoyez un message pour démarrer
 </div>
 </div>
 )}
 
 {chatHistory.length === 0 && currentSessionId ? (
             <div className="text-center py-16">
 <div className="w-12 h-12 bg-theme-hover rounded-xl flex items-center justify-center mx-auto mb-3">
 <svg className="w-6 h-6 text-theme-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4z" />
                 </svg>
               </div>
 <div className="text-sm text-theme-text-secondary font-medium">Aucune conversation</div>
 <div className="text-xs text-theme-text-tertiary mt-1">Créez votre première analyse</div>
             </div>
           ) : (
             (() => {
               // FILTRER INTELLIGEMMENT : Inclure sessions temporaires et permanentes
               const filteredSessions = chatHistory.filter(session => 
                 session.title && 
                 session.title.trim() !== '' &&
                 !session.title.includes('ephemeral') && // Exclure sessions éphémères seulement
                 !session.title.includes('Analyse 16/09') // Éviter doublons de test
               );
               
 // Logs de sessions filtrées supprimés (spam)
               
               return (

                 <div className="space-y-1">
                   {filteredSessions.map((session, index) => {
                     const isActive = currentSessionId === session.id && !isEphemeral;
                     const hasInsights = (session.insights_count || 0) > 0;
                     const hasReports = (session.reports_count || 0) > 0;
                     const isTemporary = session.isTemporary || session.id?.startsWith('temp-');
                     
                     return (
                       <div
                         key={session.id}
                         className={`group relative cursor-pointer border ${
                           isActive
 ? 'bg-theme-hover border-[var(--theme-text-primary)]'
                             : isTemporary 
 ? 'bg-theme-hover border-theme'
 : 'bg-transparent border-theme hover:bg-theme-hover'
                         }`}
                         style={{
                           animationDelay: `${index * 30}ms`
                         }}
                       >
                  <div
                    onClick={async () => {
                      try {
                        setIsEphemeral(false);
 setMobileSidebarOpen(false); // Fermer la sidebar mobile
 
 // FIX: Utiliser la fonction dédiée au lieu de dupliquer le code
 updateCurrentSession(session.id);
 await loadSessionHistory(session.id);
 
                      } catch (error) {
 logger.error('FluidChat: Erreur restauration:', error);
                      }
                    }}
                    className="flex-1"
                  >
                    <div className="p-4">
                      {/* En-tête de session */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${
                      isActive ? '' 
                      : isTemporary ? 'opacity-50' 
                      : 'bg-theme-text-tertiary'
                            }`} style={isActive || isTemporary ? { background: 'var(--spark-gold)' } : undefined}></div>
                    <div className="text-sm font-medium truncate flex-1">
                      {editingSessionId === session.id ? (
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onBlur={() => {
                            if (editingTitle.trim()) {
                              handleRenameSession(session.id, editingTitle);
                            }
                            setEditingSessionId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (editingTitle.trim()) {
                                handleRenameSession(session.id, editingTitle);
                              }
                              setEditingSessionId(null);
                            } else if (e.key === 'Escape') {
                              setEditingSessionId(null);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          className="w-full px-2 py-1 bg-white dark:bg-gray-800 border border-accent-primary rounded text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-primary/50 transition-colors"
                          maxLength={100}
                        />
                      ) : (
                        <span className="text-theme-text-primary">
                              {session.title || 'Conversation'}
                            </span>
                      )}
                    </div>
                            {isTemporary && (
                      <div className="px-1.5 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded text-[10px] text-purple-600 dark:text-purple-300 font-medium">
                                NOUVEAU
                              </div>
                            )}
                          </div>
 <div className="text-xs text-theme-text-tertiary">
                            {new Date(session.last_activity || session.created_at).toLocaleDateString('fr-FR', { 
                              weekday: 'short',
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                      </div>
                      
                      {/* Métriques de session */}
                      <div className="flex items-center space-x-4 text-xs">
                        <div className="flex items-center space-x-1.5">
 <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
 <span className="text-theme-text-secondary">{session.insights_count || 0}</span>
 <span className="text-theme-text-tertiary">insights</span>
                        </div>
                        
                        <div className="flex items-center space-x-1.5">
 <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
 <span className="text-theme-text-secondary">{session.reports_count || 0}</span>
 <span className="text-theme-text-tertiary">reports</span>
                        </div>
                        
                        <div className="flex items-center space-x-1.5">
 <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
 <span className="text-theme-text-secondary">{session.message_count || 0}</span>
 <span className="text-theme-text-tertiary">messages</span>
                        </div>
                      </div>
                      
                      {/* Indicateurs de statut */}
                      {hasInsights && (
 <div className="mt-2 flex items-center space-x-1 text-xs text-purple-600 dark:text-purple-400">
 <div className="w-1 h-1 bg-purple-500 rounded-full animate-pulse"></div>
                          <span>Insights générés</span>
                        </div>
                      )}
                    </div>
                  </div>

              {/* Actions inline (visibles au hover) */}
              <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                    setEditingSessionId(session.id);
                    setEditingTitle(session.title || 'Conversation');
                  }}
                  className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded text-blue-600 dark:text-blue-400 transition-colors"
                  title="Renommer"
                >
                  <Edit3 size={14} />
                        </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                    handleDeleteSession(session.id, session.title);
                  }}
                  className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-red-600 dark:text-red-400 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 size={14} />
                          </button>
                    </div>
                       </div>
                     );
                   })}
                 </div>
               );
             })()
           )}
 </>
           )}
         </div>
       </div>

 {/* Chat Principal - Responsive */}
 <div className={`flex-1 flex flex-col transition-all duration-500 ${isEphemeral ? 'bg-gradient-to-br from-purple-50/20 via-theme-base to-theme-base dark:from-purple-950/10 dark:via-theme-base dark:to-theme-base' : 'bg-theme-base'}`}>
 {/* Header du chat avec toggle éphémère - Responsive */}
 <div className="flex items-center justify-between p-3 lg:p-4 border-b border-theme-border bg-theme-surface/80 backdrop-blur-sm">
 
 <div className="flex items-center space-x-3">
 {/* Mobile Hamburger Menu */}
 <button
 onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
 className="md:hidden p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
 aria-label="Menu"
 >
 <Menu size={20} className="text-gray-900 dark:text-white" />
 </button>

 {/* Toggle éphémère minimaliste - Style OpenAI */}
            <button
              onClick={() => {
 // Ne pas permettre changement pendant envoi
 if (isLoading || isSendingRef.current) return;
 
                if (isEphemeral) {
 // Désactiver le mode éphémère → Créer vraie session
                  setIsEphemeral(false);
 logger.log('Mode éphémère désactivé - Historique activé');
                  if (currentSessionId?.startsWith('ephemeral-')) {
                    initializeSession().then(newSessionId => {
                      setCurrentSessionId(newSessionId);
                      loadChatHistory();
                    });
                  }
                } else {
                  // Activer le mode éphémère
 if (messages.length > 0) {
 if (!confirm('Mode éphémère : cette conversation ne sera PAS sauvegardée. Continuer ?')) {
 return;
 }
 }
                  const newSessionId = `ephemeral-${Date.now()}`;
                  setMessages([]);
                  setCurrentSessionId(newSessionId);
                  setIsEphemeral(true);
 logger.log('Mode éphémère activé - Aucune sauvegarde');
 }
 }}
 disabled={isLoading}
 className={`
 group relative p-2 rounded-xl transition-all duration-300
 ${isEphemeral 
 ? 'bg-purple-100 hover:bg-purple-200 text-purple-600 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 dark:text-purple-600 dark:text-purple-400' 
 : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
 }
 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
 `}
 title={isEphemeral ? 'Historique désactivé (mode éphémère)' : 'Historique activé (cliquer pour désactiver)'}
 >
 {/* Icône Historique/Mémoire - Style OpenAI */}
 <svg 
 className="w-5 h-5 transition-transform group-hover:scale-110" 
 fill="none" 
 viewBox="0 0 24 24" 
 stroke="currentColor"
 strokeWidth={2}
 >
 {isEphemeral ? (
 /* Icône historique barré (éphémère) */
 <>
 <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
 <path strokeLinecap="round" strokeLinejoin="round" d="M4 4l16 16" strokeWidth={2.5} />
 </>
 ) : (
 /* Icône historique normal */
 <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
 )}
 </svg>
 
 {/* Tooltip élégant au survol */}
 <div className="absolute left-full ml-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-lg">
 {isEphemeral ? ' Éphémère : Non sauvegardé' : ' Historique activé'}
              </div>
            </button>
          </div>

 {/* Boutons PDF minimalistes - Style sobre */}
 <div className="hidden sm:flex items-center space-x-1.5">
 <button
 onClick={() => setShowPDFPreview(!showPDFPreview)}
 className={`group relative p-2 rounded-lg transition-all duration-200 ${
 showPDFPreview
 ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
 : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
 } disabled:opacity-40 disabled:cursor-not-allowed`}
 disabled={messages.length === 0}
 title="Prévisualiser PDF"
 >
 <Eye className="w-4 h-4" />
 </button>
 
 <button
 onClick={exportPDF}
 className="group relative p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
 disabled={messages.length === 0}
 title="Exporter PDF"
 >
 <Download className="w-4 h-4" />
 </button>
        </div>
 </div>

 {/* PDF Preview V3 - Modal plein écran */}
 {showPDFPreview && messages.length > 0 && (
 <PDFPreviewV3
 content={messages.filter(m => m.role === 'assistant').map(m => m.content).join('\n\n')}
 options={{
 title: 'Conversation Console',
 subtitle: currentSessionId ? `Session ${currentSessionId.slice(0, 8)}` : 'Analyse IA',
 author: 'Clairify AI Analytics',
 timestamp: true
 }}
 onClose={() => setShowPDFPreview(false)}
 />
 )}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .streaming-content .last-char {
          animation: pulse 1s infinite;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        @keyframes progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-progress {
          animation: progress 1.5s ease-in-out infinite;
        }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
          background-size: 200px 100%;
          animation: shimmer 2s infinite;
        }
 
/* Scrollbar élégante - Style moderne et sobre */
.custom-scrollbar::-webkit-scrollbar {
width: 6px;
height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
background: linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%);
border-radius: 10px;
transition: all 0.3s ease;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
background: linear-gradient(180deg, #475569 0%, #334155 100%);
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
background: linear-gradient(180deg, #334155 0%, #1e293b 100%);
}
/* Firefox */
.custom-scrollbar {
scrollbar-width: thin;
scrollbar-color: #cbd5e1 transparent;
}
.dark .custom-scrollbar {
scrollbar-color: #475569 transparent;
}
      `}</style>


 {/* Messages avec style fluide - Responsive padding */}
 <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 lg:px-6 py-4 lg:py-6 space-y-4 lg:space-y-6 custom-scrollbar">
 {/* Supprimé: Indicateur d'analyses en arrière-plan (causait confusion et requêtes fantômes) */}

        {messages.length === 0 && (
          <div className="text-center py-16">
            <CreationSpark variant="mark" className="w-14 h-14 mx-auto mb-8" />
            <p className="page-kicker mb-3">{isEphemeral ? 'Éphémère' : 'Console'}</p>
            <h2 className="font-display text-3xl font-medium mb-3">
 {isEphemeral ? 'Conversation éphémère' : 'Commencez une analyse'}
            </h2>
            <p className="text-theme-secondary text-sm mb-10 max-w-md mx-auto">
              {isEphemeral 
                ? 'Cette conversation ne sera pas sauvegardée.'
 : 'Importez un fichier ou posez une question.'
              }
            </p>
            
            {isEphemeral && (
 <div className="border border-theme p-4 mb-8 max-w-md mx-auto">
                <p className="text-sm text-theme-secondary">
                  Mode éphémère. Rien n'est conservé. Basculez le toggle pour enregistrer.
                </p>
              </div>
            )}
            
 <div className="flex justify-center gap-12 text-sm">
 <div className="flex flex-col items-center">
 <span className="page-kicker mb-1">01</span>
 <span className="text-theme-secondary">Tableaux</span>
              </div>
 <div className="flex flex-col items-center">
 <span className="page-kicker mb-1">02</span>
 <span className="text-theme-secondary">Analyses</span>
 </div>
 <div className="flex flex-col items-center">
 <span className="page-kicker mb-1">03</span>
 <span className="text-theme-secondary">Rapports</span>
              </div>
            </div>
          </div>
        )}

 {messages.filter(msg => {
 // Masquer UNIQUEMENT les messages complètement vides (pas de contenu ET pas de thinking)
 if (msg.role === 'assistant' && !msg.content && !msg.thinking) {
 return false;
 }
 return true;
 }).map((message, messageIndex) => (
          <div key={message.id} className="group">
 {/* Message système discret et centré */}
 {message.role === 'system' ? (
 <div className="flex justify-center my-2">
 <div className={`px-4 py-3 rounded-lg text-sm shadow-sm ${
 message.metadata?.isError 
 ? 'bg-red-50 border border-red-200 text-red-800' 
 : 'bg-amber-50 border border-amber-200 text-amber-800'
 }`}>
 <div className="flex items-center gap-3">
 <span>{message.content}</span>
 {message.metadata?.canRetry && (
 <button
 onClick={() => {
 // Réenvoyer le message original
 setInputValue(message.metadata?.originalMessage || '');
 if (message.metadata?.originalFiles && message.metadata.originalFiles.length > 0) {
 setUploadedFile(message.metadata.originalFiles[0]);
 }
 // Supprimer le message d'erreur
 setMessages(prev => prev.filter((m: Message) => m.id !== message.id));
 // Déclencher l'envoi après un court délai
 setTimeout(() => handleSend(), 100);
 }}
 className="flex items-center gap-1 px-3 py-1 bg-white hover:bg-red-100 border border-red-300 rounded-md text-red-700 text-xs font-medium transition-colors"
 title="Réessayer"
 >
 <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
 <path d="M21 2v6h-6"></path>
 <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
 <path d="M3 22v-6h6"></path>
 <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
 </svg>
 Réessayer
 </button>
 )}
 </div>
 </div>
 </div>
 ) : (
            <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
 <div className={`max-w-4xl w-full relative ${
                message.role === 'user' 
 ? 'bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 text-theme-text-primary shadow-lg shadow-purple-500/10' 
 : 'bg-theme-surface border border-theme-border shadow-theme-sm'
 } rounded-xl p-4 md:p-6 overflow-hidden`}>
                {message.role === 'user' ? (
                  <div>
 {/* FICHIER ATTACHÉ - Style sobre et élégant inline */}
 {message.metadata?.attachedFile && (
 <div className="mb-2 inline-flex items-center gap-2 px-3 py-1.5 bg-theme-hover/50 dark:bg-theme-hover/30 rounded-lg border border-theme-border/50 text-theme-secondary text-sm">
 <svg className="w-4 h-4 text-theme-secondary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
 </svg>
 <span className="font-medium text-theme-primary truncate max-w-[200px]">
 {message.metadata.attachedFile.name}
 </span>
 <span className="text-xs text-theme-secondary/70">
 {(message.metadata.attachedFile.size / 1024).toFixed(1)} KB
 </span>
 </div>
 )}
 
 {/* Message texte - Sans débordement */}
 <div className="whitespace-pre-wrap leading-relaxed break-words overflow-wrap-anywhere max-w-full overflow-x-hidden text-gray-900 dark:text-gray-100 font-medium">
 {message.content}
 </div>
 
 {/* Actions User Message - Style Claude sobre et intuitif */}
 <div className="absolute -bottom-2 right-4 flex items-center space-x-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button
                          onClick={async () => {
 // NOUVEAU: Si génération en cours, annuler élégamment puis éditer
 if (isLoading || isSendingRef.current) {
 // Confirmer annulation avec message clair
 const shouldCancel = window.confirm(
   '⚠️ Génération en cours\n\n' +
   'Voulez-vous annuler la génération actuelle pour modifier ce message ?\n\n' +
   'La réponse en cours sera perdue.'
 );
 if (!shouldCancel) {
 return;
 }
 
 logger.log(' Annulation pour édition');
 
 // Annuler la requête HTTP
 if (abortControllerRef.current) {
 abortControllerRef.current.abort();
 }
 
 // Capturer thinking si présent
 const thinkingSnapshot = currentThinking;
 
 // Reset states
 setIsLoading(false);
 setCurrentThinking('');
 setAgentSteps([]);
 setAgentMode(false);
 isSendingRef.current = false;
 
 // Supprimer message assistant en cours
 if (streamingMessageIdRef.current) {
 // Sauvegarder comme partial si thinking présent
 if (thinkingSnapshot && thinkingSnapshot.length > 10) {
 setMessages(prev => prev.map(msg => 
 msg.id === streamingMessageIdRef.current 
 ? { ...msg, thinking: thinkingSnapshot }
 : msg
 ));
 } else {
 // Sinon supprimer complètement
 setMessages(prev => prev.filter(m => m.id !== streamingMessageIdRef.current));
 }
 }
 
 streamingMessageIdRef.current = null;
 
 // Attendre reset
 await new Promise(resolve => setTimeout(resolve, 100));
 }
 
 // Trouver l'index de ce message
 const messageIdx = messages.findIndex(m => m.id === message.id);
 
 logger.log(' [EDIT] Début édition message', { 
 messageIdx, 
 totalMessages: messages.length,
 messagesWillBeDeleted: messages.length - messageIdx,
 content: message.content.substring(0, 50) + '...'
 });
 
 // BLOQUER rechargement pendant édition
 isEditingRef.current = true;
 
 // FIX: Supprimer CE message et TOUS ceux après (inclus)
 const messagesBefore = messages.slice(0, messageIdx);
 setMessages(messagesBefore);
 
 logger.log(' [EDIT] Messages supprimés, nouveau total:', messagesBefore.length);
 
 // Remettre le contenu dans l'input (sans le badge fichier)
 setInputValue(message.content);
 
 // Restaurer le fichier si présent dans metadata
 if (message.metadata?.attachedFile) {
 logger.log(' [EDIT] Fichier restauré:', message.metadata.attachedFile.name);
 setUploadedFile({
 name: message.metadata.attachedFile.name,
 size: message.metadata.attachedFile.size,
 type: message.metadata.attachedFile.type || '',
 status: 'uploaded',
 fullContent: '',
 fileKey: message.metadata.attachedFile.fileKey,
 fileId: message.metadata.attachedFile.fileId,
 orgPath: message.metadata.attachedFile.orgPath
 });
 // Nettoyer l'input file pour permettre re-upload si nécessaire
 if (fileInputRef.current) {
   fileInputRef.current.value = '';
 }
 } else {
 // S'assurer de vider le fichier s'il n'y en avait pas
 setUploadedFile(null);
 if (fileInputRef.current) {
   fileInputRef.current.value = '';
 }
 }
 
 // Focus sur l'input
 setTimeout(() => {
 textareaRef.current?.focus();
 textareaRef.current?.setSelectionRange(message.content.length, message.content.length);
 logger.log(' [EDIT] Focus donné à l\'input - Édition terminée (PAS D\'ENVOI AUTO)');
 }, 100);
 }}
title={isLoading ? "Annuler la génération et modifier" : "Modifier ce message"}
className="p-2 bg-theme-surface hover:bg-theme-hover border border-theme-border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 group/edit"
>
<Edit3 className="w-4 h-4 text-theme-tertiary group-hover/edit:text-purple-600 dark:group-hover/edit:text-purple-400 transition-colors" />
 </button>
                          </div>
 </div>
 ) : (
 <div>
 {/* AFFICHER LE THINKING avec ThinkingAgentV2 (collapsed par défaut) */}
 {thinkingMode && message.thinking && !isLoading && (() => {
 // Parser les steps depuis le thinking
 const thinkingSteps = message.thinking?.split('\n').filter(l => l.trim()).slice(0, 20).map((line, i) => ({
 id: `step-${message.id}-${i}`,
 title: line.length > 50 ? line.substring(0, 47) + '...' : line,
 status: 'completed' as const,
 description: line,
 action: ''
 })) || [];
 
 return (
 <div className="mb-6">
 <ThinkingAgentV2
 steps={thinkingSteps}
 currentThinkingContent={message.thinking || ''}
 isComplete={true}
 />
 </div>
 );
 })()}
 
 {/* CONTENU DU MESSAGE (Rapport final) */}
 <div className="streaming-content">
 {/* NOUVEAU: Actions élégantes pour analyses interrompues */}
 {!message.content && message.thinking && (
 <div className="space-y-3">
<div className="relative overflow-hidden rounded-xl border-2 border-amber-300/60 dark:border-amber-700/60 bg-gradient-to-br from-amber-50/90 via-orange-50/80 to-yellow-50/90 dark:from-amber-950/50 dark:via-orange-950/40 dark:to-yellow-950/50 shadow-lg backdrop-blur-sm">
{/* Barre de progression interrompue */}
<div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 opacity-50"></div>

<div className="relative flex items-center justify-between py-5 px-6">
<div className="flex items-center space-x-4 flex-1 min-w-0">
{/* Icône avec animation pulse */}
<div className="relative flex-shrink-0">
<div className="absolute inset-0 rounded-xl bg-amber-400/20 dark:bg-amber-500/30 animate-pulse"></div>
<div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400/30 to-indigo-400/30 dark:from-amber-500/40 dark:to-indigo-500/40 flex items-center justify-center border-2 border-amber-400/50 dark:border-amber-600/50 shadow-md">
<AlertCircle className="w-6 h-6 text-amber-700 dark:text-amber-300" />
</div>
</div>
{/* Texte avec hiérarchie améliorée */}
<div className="flex-1 min-w-0">
<span className="text-base font-bold text-amber-900 dark:text-amber-100 block mb-1.5">
Analyse interrompue
</span>
<span className="text-sm text-amber-800 dark:text-amber-300 font-medium leading-relaxed">
Vous pouvez continuer l'analyse ou copier le résultat partiel
</span>
{/* Indicateur visuel */}
<div className="mt-2 flex items-center space-x-2 text-xs text-amber-700 dark:text-amber-400">
<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
</svg>
<span>L'analyse a été interrompue avant la fin</span>
</div>
</div>
</div>
<div className="flex items-center space-x-2 flex-shrink-0">
                            <button
 onClick={async () => {
 // Reprendre l'analyse avec le contexte thinking
 const lastUserMsg = messages.find(m => m.role === 'user' && m.timestamp < message.timestamp);
 if (!lastUserMsg) {
 logger.warn('Impossible de trouver le message user original');
 return;
 }
 
 // Supprimer le message interrompu
 setMessages(prev => prev.filter(m => m.id !== message.id));
 
 // Remettre le message user dans l'input
 setInputValue(lastUserMsg.content);
 
 // Restaurer fichier si présent
 if (lastUserMsg.metadata?.attachedFile) {
 setUploadedFile({
 name: lastUserMsg.metadata.attachedFile.name,
 size: lastUserMsg.metadata.attachedFile.size,
 type: lastUserMsg.metadata.attachedFile.type || '',
 status: 'uploaded',
 fullContent: ''
 });
 }
 
 // Relancer automatiquement après un micro-délai
 setTimeout(() => {
 handleSend();
 }, 100);
 }}
 disabled={isLoading}
className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
title="Continuer l'analyse"
>
<Play className="w-4 h-4" />
<span>Continuer</span>
                            </button>
 <button
 onClick={() => {
 navigator.clipboard.writeText(message.thinking || '');
 }}
className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-sm font-medium text-gray-700 dark:text-gray-300"
title="Copier l'analyse partielle"
>
<Copy className="w-4 h-4" />
<span>Copier</span>
                        </button>
                          </div>
</div>
</div>
 
{/* Aperçu du thinking - Ultra-intuitif avec preview visuel */}
<details className="group relative overflow-hidden rounded-xl border-2 border-amber-300/50 dark:border-amber-700/50 bg-gradient-to-br from-amber-50/80 via-orange-50/60 to-yellow-50/80 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-yellow-950/40 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:border-amber-400/70 dark:hover:border-amber-600/70">
<summary className="px-5 py-4 cursor-pointer select-none flex items-center justify-between hover:bg-amber-100/60 dark:hover:bg-amber-900/30 transition-all duration-200 active:scale-[0.98]">
<div className="flex items-center space-x-4 flex-1 min-w-0">
{/* Icône avec badge de notification */}
<div className="relative flex-shrink-0">
<div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400/30 to-indigo-400/30 dark:from-amber-500/40 dark:to-indigo-500/40 flex items-center justify-center border-2 border-amber-400/50 dark:border-amber-600/50 shadow-md group-hover:scale-110 transition-transform duration-200">
<Brain className="w-5 h-5 text-amber-700 dark:text-amber-300" />
</div>
{/* Badge notification */}
<div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-purple-600 dark:from-purple-500 to-red-500 rounded-full border-2 border-white dark:border-gray-900 flex items-center justify-center shadow-lg">
<span className="text-[8px] font-bold text-white">{message.thinking.split('\n').length}</span>
</div>
</div>
{/* Texte avec hiérarchie claire */}
<div className="flex-1 min-w-0">
<span className="text-sm font-bold text-amber-900 dark:text-amber-100 block mb-1">
Analyse détaillée disponible
</span>
<div className="flex items-center space-x-2 text-xs text-amber-700 dark:text-amber-400">
<span className="font-medium">{message.thinking.split('\n').length} étapes d'analyse</span>
<span>•</span>
<span>Cliquez pour voir</span>
</div>
{/* Preview du premier ligne du thinking */}
<div className="mt-2 text-xs text-amber-800/70 dark:text-amber-300/70 font-medium truncate max-w-md">
{message.thinking.split('\n')[0]?.substring(0, 60) || 'Analyse en cours...'}...
</div>
</div>
</div>
{/* Flèche animée */}
<div className="flex-shrink-0 ml-4">
<div className="w-8 h-8 rounded-lg bg-amber-200/50 dark:bg-amber-800/50 flex items-center justify-center group-hover:bg-amber-300/60 dark:group-hover:bg-amber-700/60 transition-colors">
<svg className="w-5 h-5 text-amber-700 dark:text-amber-300 transition-transform duration-300 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
</svg>
</div>
</div>
</summary>
{/* Contenu avec animation d'ouverture */}
<div className="px-5 py-4 border-t-2 border-amber-200/60 dark:border-amber-800/60 bg-white/70 dark:bg-gray-900/50 animate-fadeIn">
<div className="relative">
{/* Header du contenu */}
<div className="flex items-center justify-between mb-3 pb-2 border-b border-amber-200/40 dark:border-amber-800/40">
<span className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide">
Réflexion de l'IA
</span>
<button
onClick={(e) => {
e.preventDefault();
navigator.clipboard.writeText(message.thinking || '');
}}
className="flex items-center space-x-1.5 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 rounded-md transition-all duration-200"
>
<Copy className="w-3.5 h-3.5" />
<span>Copier</span>
</button>
</div>
{/* Contenu avec syntax highlighting visuel */}
<div className="relative">
<div className="text-xs text-gray-800 dark:text-gray-200 font-mono whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto custom-scrollbar pr-3 pl-4 py-2 bg-gray-50/50 dark:bg-gray-800/30 rounded-lg border border-gray-200/50 dark:border-gray-700/50">
{message.thinking.substring(0, 1500)}{message.thinking.length > 1500 ? (
<>
...
<div className="mt-3 pt-3 border-t border-gray-300/50 dark:border-gray-600/50 text-center">
<button
onClick={(e) => {
e.preventDefault();
navigator.clipboard.writeText(message.thinking || '');
}}
className="text-xs text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-semibold flex items-center space-x-1.5 mx-auto transition-colors"
>
<Copy className="w-3.5 h-3.5" />
<span>Voir l'analyse complète ({message.thinking.length} caractères)</span>
</button>
</div>
</>
) : ''}
</div>
</div>
</div>
</div>
</details>
                      </div>
                    )}
 
 {/* Contenu avec graphiques intégrés - ULTRA ROBUSTE */}
 {message.content && (() => {
 // APPROCHE PARSING MANUEL pour gérer les accolades imbriquées correctement
 
 // Fonction pour extraire un JSON complet en comptant les accolades
 // AMÉLIORÉE: Limite de recherche plus élevée pour JSON longs
 const extractCompleteJSON = (text: string, startIndex: number, maxSearchLength: number = 50000): { json: string, endIndex: number } | null => {
 let braceCount = 0;
 let inString = false;
 let escapeNext = false;
 let jsonStart = -1;
 
 // Limiter la recherche pour éviter les boucles infinies
 const searchEnd = Math.min(startIndex + maxSearchLength, text.length);
 
 for (let i = startIndex; i < searchEnd; i++) {
 const char = text[i];
 
 if (escapeNext) {
 escapeNext = false;
 continue;
 }
 
 if (char === '\\') {
 escapeNext = true;
 continue;
 }
 
 if (char === '"') {
 inString = !inString;
 continue;
 }
 
 if (inString) continue;
 
 if (char === '{') {
 if (braceCount === 0) jsonStart = i;
 braceCount++;
 } else if (char === '}') {
 braceCount--;
 if (braceCount === 0 && jsonStart >= 0) {
 const json = text.substring(jsonStart, i + 1);
 
 // DEBUG: Logger si JSON très long
 if (json.length > 1000) {
 logger.log('[Chat] JSON extrait (long):', {
 length: json.length,
 startIndex: jsonStart,
 endIndex: i + 1,
 preview: json.substring(0, 100) + '...'
 });
 }
 
 return {
 json: json,
 endIndex: i + 1
 };
 }
 }
 }
 
 // Si on arrive ici sans avoir trouvé la fin, essayer quand même de retourner ce qu'on a
 if (jsonStart >= 0 && braceCount > 0) {
 logger.warn('[Chat] JSON incomplet détecté, tentative de réparation:', {
 startIndex: jsonStart,
 braceCount: braceCount,
 preview: text.substring(jsonStart, Math.min(jsonStart + 200, text.length))
 });
 }
 
 return null;
 };
 
 // FONCTION: Réparer JSON mal formé de graphiques (ULTRA ROBUSTE)
 const repairChartJSON = (jsonStr: string): string => {
 try {
 // Nettoyer le JSON de base
 let cleaned = jsonStr
 .replace(/\+(\d+)/g, '$1') // Retirer les + devant les nombres
 .replace(/,\s*}/g, '}') // Retirer virgules trainantes
 .replace(/,\s*]/g, ']'); // Retirer virgules trainantes dans arrays
 
 // Essayer de parser directement
 try {
 const parsed = JSON.parse(cleaned);
 if (parsed.type && parsed.data) return cleaned; // Déjà valide
 } catch (e) {
 // Pas encore valide, continuer réparation
 }
 
 // Détecter le type de graphique - SUPPORT TOUS TYPES
 // Pattern COMPLET avec tous les types supportés
 const typeMatch = cleaned.match(new RegExp(`"type"\\s*:\\s*"(${supportedChartTypes.join('|')})"`, 'i'));
 if (!typeMatch) return jsonStr;
 
 const chartType = typeMatch[1];
 
 // Extraire le title si présent (avant le premier {)
 const titleMatch = cleaned.match(/"title"\s*:\s*"([^"]+)"/);
 const title = titleMatch ? titleMatch[1] : 'Graphique';
 
 // Si "data" est déjà présent, juste nettoyer et valider
 if (cleaned.includes('"data"')) {
 try {
 JSON.parse(cleaned); // Valider que c'est parsable
 return cleaned;
 } catch (e) {
 // Si pas parsable malgré "data", continuer reconstruction
 }
 }
 
 // RECONSTRUCTION COMPLÈTE: extraire tous les objets { ... }
 const dataObjects: any[] = [];
 let depth = 0;
 let currentObj = '';
 let inString = false;
 let startedData = false;
 
 for (let i = 0; i < cleaned.length; i++) {
 const char = cleaned[i];
 const prevChar = i > 0 ? cleaned[i-1] : '';
 
 // Gérer les strings
 if (char === '"' && prevChar !== '\\') {
 inString = !inString;
 }
 
 if (inString) {
 if (startedData) currentObj += char;
 continue;
 }
 
 // Détecter début d'objet APRÈS le type
 if (char === '{') {
 depth++;
 if (depth === 2) { // Premier objet dans data
 startedData = true;
 currentObj = '{';
 } else if (startedData) {
 currentObj += char;
 }
 } else if (char === '}') {
 if (startedData) currentObj += char;
 depth--;
 
 // Objet data terminé
 if (depth === 1 && startedData && currentObj.length > 2) {
 try {
 const obj = JSON.parse(currentObj.replace(/\+(\d+)/g, '$1'));
 dataObjects.push(obj);
 currentObj = '';
 startedData = false;
 } catch (e) {
 // Objet invalide, continuer
 }
 }
 } else if (startedData) {
 currentObj += char;
 }
 }
 
 // Si on a extrait des objets, reconstruire le JSON complet
 if (dataObjects.length > 0) {
 const reconstructed = {
 type: chartType,
 title: title,
 data: dataObjects
 };
 return JSON.stringify(reconstructed);
 }
 
 // Sinon retourner l'original nettoyé
 return cleaned;
 
 } catch (e) {
 logger.warn('Réparation JSON échouée:', e);
 return jsonStr;
 }
 };
 
 // Détecter toutes les occurrences de graphiques avec leurs positions exactes - ULTRA-ROBUSTE
 const chartMatches: Array<{json: string, startIndex: number, endIndex: number}> = [];
 // Liste COMPLÈTE de tous les types supportés - IDENTIQUE à PDF
 const supportedChartTypes = [
   'bar', 'pie', 'line', 'scatter', // Types de base
   'chart', 'horizontal_bar', 'waterfall', 'area', 'gantt', // Variantes bar/line
   'doughnut', 'polarArea', // Variantes pie
   'stacked_bar', 'radar', 'bubble' // Autres types
 ];
 const chartTypePattern = `(${supportedChartTypes.join('|')})`;

 // Pattern 1: SPECIFICATION JSON: {...}
 // Utiliser matchAll pour trouver TOUS les matches (plus robuste que exec avec regex global)
 const specPattern = /SPECIFICATION\s+JSON:\s*/gi;
 const allSpecMatches = Array.from(message.content.matchAll(specPattern));
 
 for (const specMatch of allSpecMatches) {
 if (!specMatch || specMatch.index === undefined) continue;
 
 const searchStart = specMatch.index + specMatch[0].length;
 const result = extractCompleteJSON(message.content, searchStart);
 
 if (result) {
 // Vérifier que c'est bien un chart avant de réparer
 if (new RegExp(`"type"\\s*:\\s*"${chartTypePattern}"`, 'i').test(result.json)) {
 const repairedJSON = repairChartJSON(result.json);
 
 // Vérifier qu'on ne l'a pas déjà ajouté (éviter doublons)
 const alreadyAdded = chartMatches.some(m => 
 Math.abs(m.startIndex - specMatch.index!) < 20 || 
 Math.abs(m.endIndex - result.endIndex) < 20
 );
 
 if (!alreadyAdded) {
 chartMatches.push({
 json: repairedJSON,
 startIndex: specMatch.index,
 endIndex: result.endIndex
 });
 
        // DEBUG: Logger pour diagnostiquer (DISABLED - too verbose)
        // logger.log('[Chat] Graphique détecté (Pattern 1 - SPECIFICATION JSON):', {
        // title: JSON.parse(repairedJSON).title || 'Sans titre',
        // type: JSON.parse(repairedJSON).type,
        // startIndex: specMatch.index,
        // endIndex: result.endIndex
        // });
 }
 }
 }
 }

 // Pattern 2: ```json\n{...}\n```
 const codeBlockPattern = /```json\s*/gi;
 const allCodeMatches = Array.from(message.content.matchAll(codeBlockPattern));
 
 for (const codeMatch of allCodeMatches) {
 if (!codeMatch || codeMatch.index === undefined) continue;
 
 const searchStart = codeMatch.index + codeMatch[0].length;
 const result = extractCompleteJSON(message.content, searchStart);
 
 if (result) {
 // Vérifier que c'est bien un chart
 if (new RegExp(`"type"\\s*:\\s*"${chartTypePattern}"`, 'i').test(result.json)) {
 const repairedJSON = repairChartJSON(result.json);
 const endMarker = message.content.indexOf('```', result.endIndex);
 const finalEndIndex = endMarker > -1 ? endMarker + 3 : result.endIndex;
 
 // Vérifier qu'on ne l'a pas déjà ajouté (éviter doublons)
 const alreadyAdded = chartMatches.some(m => 
 Math.abs(m.startIndex - codeMatch.index!) < 20 || 
 Math.abs(m.endIndex - finalEndIndex) < 20
 );
 
 if (!alreadyAdded) {
 chartMatches.push({
 json: repairedJSON,
 startIndex: codeMatch.index,
 endIndex: finalEndIndex
 });
 
            // logger.log('[Chat] Graphique détecté (Pattern 2 - ```json):', {
            // title: JSON.parse(repairedJSON).title || 'Sans titre',
            // type: JSON.parse(repairedJSON).type
            // });
 }
 }
 }
 }

 // Pattern 3: ```chart\n{...}\n``` (format alternatif)
 const chartBlockPattern = /```chart\s*/gi;
 const allChartBlockMatches = Array.from(message.content.matchAll(chartBlockPattern));
 
 for (const chartBlockMatch of allChartBlockMatches) {
 if (!chartBlockMatch || chartBlockMatch.index === undefined) continue;
 
 const searchStart = chartBlockMatch.index + chartBlockMatch[0].length;
 const result = extractCompleteJSON(message.content, searchStart);
 
 if (result) {
 if (new RegExp(`"type"\\s*:\\s*"${chartTypePattern}"`, 'i').test(result.json)) {
 const repairedJSON = repairChartJSON(result.json);
 const endMarker = message.content.indexOf('```', result.endIndex);
 const finalEndIndex = endMarker > -1 ? endMarker + 3 : result.endIndex;
 
 // Vérifier qu'on ne l'a pas déjà ajouté (éviter doublons)
 const alreadyAdded = chartMatches.some(m => 
 Math.abs(m.startIndex - chartBlockMatch.index!) < 20 || 
 Math.abs(m.endIndex - finalEndIndex) < 20
 );
 
 if (!alreadyAdded) {
 chartMatches.push({
 json: repairedJSON,
 startIndex: chartBlockMatch.index,
 endIndex: finalEndIndex
 });
 
            // logger.log('[Chat] Graphique détecté (Pattern 3 - ```chart):', {
            // title: JSON.parse(repairedJSON).title || 'Sans titre',
            // type: JSON.parse(repairedJSON).type
            // });
 }
 }
 }
 }

 // Pattern 4: JSON nu dans le texte (sans backticks) - Détection robuste
 const nakedJsonPattern = new RegExp(`\\{\\s*"type"\\s*:\\s*"${chartTypePattern}"[\\s\\S]*?"data"\\s*:\\s*\\[[\\s\\S]*?\\][\\s\\S]*?\\}`, 'gi');
 let nakedMatch: RegExpExecArray | null;
 while ((nakedMatch = nakedJsonPattern.exec(message.content)) !== null) {
 try {
 if (!nakedMatch) continue;
 const result = extractCompleteJSON(message.content, nakedMatch.index);
 if (result && new RegExp(`"type"\\s*:\\s*"${chartTypePattern}"`, 'i').test(result.json)) {
 const repairedJSON = repairChartJSON(result.json);
 // Vérifier qu'on ne l'a pas déjà ajouté (éviter doublons)
 const alreadyAdded = chartMatches.some(m => 
 Math.abs(m.startIndex - nakedMatch!.index) < 10
 );
 if (!alreadyAdded) {
 chartMatches.push({
 json: repairedJSON,
 startIndex: nakedMatch.index,
 endIndex: result.endIndex
 });
 }
 }
 } catch (e) {
 // Ignorer les erreurs de parsing pour ce pattern
 }
 }
 
 // Trier par position et construire les parties
 chartMatches.sort((a, b) => a.startIndex - b.startIndex);
 
 let contentParts: Array<{type: 'text' | 'chart', content: any}> = [];
 let lastIndex = 0;
 
 for (const match of chartMatches) {
 // Texte avant le graphique
 if (lastIndex < match.startIndex) {
 let textContent = message.content.substring(lastIndex, match.startIndex).trim();
 
 // Nettoyer les marqueurs de JSON qui pourraient rester
 textContent = textContent
 .replace(/SPECIFICATION\s+JSON:\s*$/i, '') // Retirer "SPECIFICATION JSON:" seul
 .replace(/```json\s*$/i, '') // Retirer "```json" seul
 .trim();
 
 if (textContent) {
 contentParts.push({type: 'text', content: textContent});
 }
 }
 
 // Parser et valider le graphique
 try {
 const chartSpec = JSON.parse(match.json);
 
 // SUPPORT TOUS TYPES DE CHARTS - Liste exhaustive
 // Liste COMPLÈTE de tous les types supportés - IDENTIQUE partout
 const supportedChartTypes = [
   'bar', 'pie', 'line', 'scatter', // Types de base
   'chart', 'horizontal_bar', 'waterfall', 'area', 'gantt', // Variantes bar/line
   'doughnut', 'polarArea', // Variantes pie
   'stacked_bar', 'radar', 'bubble' // Autres types
 ];
 if (chartSpec.type && supportedChartTypes.includes(chartSpec.type.toLowerCase()) && chartSpec.data) {
 // Vérifier qu'on n'a pas déjà ajouté ce graphique (éviter doublons)
 // Comparer par titre et type pour détecter les doublons
 const isDuplicate = contentParts.some(part => 
 part.type === 'chart' && 
 part.content.title === chartSpec.title && 
 part.content.type === chartSpec.type
 );
 if (!isDuplicate) {
 contentParts.push({
 type: 'chart',
 content: {
 ...chartSpec,
 id: `chart-${contentParts.length}`
 }
 });
        } else {
          // logger.log('[Chat] Graphique dupliqué ignoré:', chartSpec.title);
        }
 }
 } catch (e) {
 logger.warn('Erreur parsing JSON graphique:', e);
 }
 
 lastIndex = match.endIndex;
 }
 
 // Texte restant
 if (lastIndex < message.content.length) {
 const textContent = message.content.substring(lastIndex).trim();
 if (textContent) {
 contentParts.push({type: 'text', content: textContent});
 }
 }
 
 // Si aucun graphique détecté, afficher normalement (mais masquer JSON bruts)
 if (contentParts.length === 0 || contentParts.every(p => p.type === 'text')) {
 // Masquer les blocs JSON bruts pendant l'affichage
 const cleanContent = message.content
 .replace(/```json\s*\{[\s\S]*?\}\s*```/g, '') // Retirer ```json {...} ```
 .replace(/```chart\s*\{[\s\S]*?\}\s*```/g, '') // Retirer ```chart {...} ```
 .replace(/SPECIFICATION\s+JSON:\s*\{[\s\S]*?\}/g, '') // Retirer SPECIFICATION JSON: {...}
 // Retirer JSON seuls avec tous les types supportés
 .replace(new RegExp(`\\n\\s*\\{\\s*"type":\\s*"(${supportedChartTypes.join('|')})"[\\s\\S]*?\\}\\s*\\n`, 'g'), '\n');
 
 return (
 <div className="prose prose-slate dark:prose-invert max-w-none prose-table:table-auto prose-th:!bg-gray-900 dark:prose-th:!bg-gray-800 prose-th:!text-white prose-th:font-bold prose-th:p-4 prose-td:p-4 prose-td:!bg-white dark:prose-td:!bg-gray-900 prose-td:!text-gray-900 dark:prose-td:!text-white prose-td:border-gray-300 dark:prose-td:border-gray-700 prose-table:border prose-table:!border-gray-300 dark:prose-table:!border-gray-700 prose-table:rounded-xl !text-gray-900 dark:!text-white leading-relaxed prose-headings:!text-gray-900 dark:prose-headings:!text-white prose-headings:font-bold prose-strong:!text-gray-900 dark:prose-strong:!text-white prose-strong:font-bold prose-p:!text-gray-900 dark:prose-p:!text-white prose-li:!text-gray-900 dark:prose-li:!text-white prose-code:!text-purple-700 dark:prose-code:!text-purple-300 break-words overflow-wrap-anywhere">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                        >
 {cleanContent}
 </ReactMarkdown>
 </div>
 );
 }
 
 // Rendre texte + graphiques dans l'ordre naturel du rapport
 return (
 <div>
 {contentParts.map((part, idx) => {
 if (part.type === 'text') {
 return (
 <div key={`text-${idx}`} className="prose prose-slate dark:prose-invert max-w-none prose-table:table-auto prose-th:!bg-gray-900 dark:prose-th:!bg-gray-800 prose-th:!text-white prose-th:font-bold prose-th:p-4 prose-td:p-4 prose-td:!bg-white dark:prose-td:!bg-gray-900 prose-td:!text-gray-900 dark:prose-td:!text-white prose-td:border-gray-300 dark:prose-td:border-gray-700 prose-table:border prose-table:!border-gray-300 dark:prose-table:!border-gray-700 prose-table:rounded-xl !text-gray-900 dark:!text-white leading-relaxed prose-headings:!text-gray-900 dark:prose-headings:!text-white prose-headings:font-bold prose-strong:!text-gray-900 dark:prose-strong:!text-white prose-strong:font-bold prose-p:!text-gray-900 dark:prose-p:!text-white prose-li:!text-gray-900 dark:prose-li:!text-white prose-code:!text-purple-700 dark:prose-code:!text-purple-300 break-words overflow-wrap-anywhere">
 <ReactMarkdown
 remarkPlugins={[remarkGfm]}
 rehypePlugins={[rehypeRaw]}
 >
 {part.content}
                        </ReactMarkdown>
                      </div>
 );
 } else {
 // Graphique interactif
 return chartsMode ? (
 <div key={`chart-${idx}`} className="my-6">
 <InteractiveChart
 type={part.content.type}
 data={part.content.data}
 title={part.content.title || 'Graphique'}
 insight={part.content.insight}
 />
 </div>
 ) : null;
 }
 })}
 </div>
 );
 })()}
 
 {/* ANCIEN CODE - Graphiques en bas (désactivé car on les intègre maintenant dans le texte) */}
 {false && chartsMode && (
                        <div className="mt-6 space-y-4">
                          {(() => {
                            // Liste COMPLÈTE de tous les types supportés
                            const supportedChartTypes = [
                              'bar', 'pie', 'line', 'scatter', // Types de base
                              'chart', 'horizontal_bar', 'waterfall', 'area', // Variantes bar/line
                              'doughnut', 'polarArea', // Variantes pie
                              'stacked_bar', 'radar', 'bubble' // Autres types
                            ];
                            
                            // Patterns multiples pour détecter tous types de graphiques - ULTRA-ROBUSTE
                            const patterns = [
                              // Pattern pour SPECIFICATION JSON: {...} (SANS backticks - format du prompt)
                              /SPECIFICATION\s+JSON:\s*(\{[^{}]*\{[^{}]*\}[^{}]*\})/g,
                              // Patterns avec TOUS les types supportés
                              new RegExp(`\`\`\`json\\s*(\\{[\\s\\S]*?"type":\\s*"(${supportedChartTypes.join('|')})"[\\s\\S]*?\\})\\s*\`\`\``, 'g'),
                              // Pattern pour JSON sans backticks (cas fréquent)
                              new RegExp(`\\{\\s*"type":\\s*"(${supportedChartTypes.join('|')})"[\\s\\S]*?"data":\\s*\\[[\\s\\S]*?\\][\\s\\S]*?\\}`, 'g'),
                              /```chart\s*(\{[\s\S]*?\})\s*```/g,
                              new RegExp(`\`\`\`data\\s*(\\{[\\s\\S]*?"type":\\s*"(${supportedChartTypes.join('|')})"[\\s\\S]*?\\})\\s*\`\`\``, 'g'),
                              // Pattern pour graphiques en blocs de texte
                              new RegExp(`(?:\\\`\\\`\\\`json\\s*)?(\\{[\\s\\S]*?"type":\\s*"(${supportedChartTypes.join('|')})"[\\s\\S]*?"data":\\s*\\[[\\s\\S]*?\\][\\s\\S]*?\\})(?:\\s*\\\`\\\`\\\`)?`, 'g')
                            ];
                            
                            let allMessageCharts: any[] = [];
                            
                            for (const pattern of patterns) {
                              const matches = Array.from(message.content.matchAll(pattern));
                              
                              for (const match of matches) {
                                try {
                                  let jsonContent = match[1] || match[0]; // Fallback sur match complet
                                  jsonContent = jsonContent.trim();
                                  
                                  // VALIDATION PRÉALABLE - Vérifier que c'est du JSON potentiel
                                  if (!jsonContent || jsonContent.length < 20 || !jsonContent.startsWith('{') || !jsonContent.endsWith('}') || !jsonContent.includes('"type"')) {
                                    // Log seulement en mode debug pour éviter le spam
                                    if (process.env.NODE_ENV === 'development' && jsonContent.length > 20) {
 logger.log('Contenu JSON filtré:', jsonContent.substring(0, 30) + '...');
                                    }
                                    continue;
                                  }
                                  
                                  // Nettoyage robuste du JSON
                                  jsonContent = jsonContent
                                    .replace(/,\s*\}/, '}') // Enlever virgules trainantes
                                    .replace(/,\s*\]/, ']') // Enlever virgules trainantes dans arrays
                                    .replace(/\n/g, ' ') // Supprimer les retours à la ligne
                                    .replace(/\s+/g, ' '); // Normaliser les espaces
                                  
                                  const chartSpec = JSON.parse(jsonContent);
                                  
 // Vérifier que c'est un graphique valide - SUPPORT TOUS TYPES
 // Liste COMPLÈTE de tous les types supportés - IDENTIQUE partout
 const supportedChartTypes = [
   'bar', 'pie', 'line', 'scatter', // Types de base
   'chart', 'horizontal_bar', 'waterfall', 'area', 'gantt', // Variantes bar/line
   'doughnut', 'polarArea', // Variantes pie
   'stacked_bar', 'radar', 'bubble' // Autres types
 ];
 if (chartSpec.type && supportedChartTypes.includes(chartSpec.type.toLowerCase())) {
                                    // VALIDATION SPÉCIALE POUR GRAPHIQUES PIE
                                    if (chartSpec.type === 'pie') {
                                      // S'assurer que les données pie ont la bonne structure
                                      if (chartSpec.data && Array.isArray(chartSpec.data)) {
                                        chartSpec.data = chartSpec.data.map((item: any, index: number) => ({
                                          label: item.label || item.name || item.produit || `Segment ${index + 1}`,
                                          value: Number(item.value || item.ca || item.amount || 0)
                                        }));
                                      }
                                    }
                                    
                                    // Données par défaut si manquantes
                                    if (!chartSpec.data || chartSpec.data.length === 0) {
                                      if (chartSpec.type === 'pie') {
                                        chartSpec.data = [
                                          { label: 'Catégorie A', value: 40 },
                                          { label: 'Catégorie B', value: 30 },
                                          { label: 'Catégorie C', value: 20 },
                                          { label: 'Autres', value: 10 }
                                        ];
                                      } else {
                                        chartSpec.data = [
                                          { label: 'Jan', value: 65 },
                                          { label: 'Fév', value: 80 },
                                          { label: 'Mar', value: 75 },
                                          { label: 'Avr', value: 90 }
                                        ];
                                      }
                                    }
                                    
                                    // Titre par défaut
                                    if (!chartSpec.title) {
                                      chartSpec.title = chartSpec.type === 'pie' ? 'Répartition des données' : 'Analyse des tendances';
                                    }
                                    
                                    allMessageCharts.push({
                                      ...chartSpec,
                                      id: `msg-chart-${message.id}-${allMessageCharts.length}`
                                    });
                                  }
                                } catch (e) {
                                  // Log moins bruyant pour éviter le spam dans la console
                                  if (process.env.NODE_ENV === 'development') {
 const error = e as Error;
 const errorMsg = error.message || String(e);
 logger.log('Parsing chart échoué (normal si pas de graphique):', {
 error: errorMsg,
                                      content: (match[1] || match[0])?.substring(0, 50) + '...'
                                    });
                                  }
                                }
                              }
                            }
                            
                            // Rendu des graphiques trouvés
                            return allMessageCharts.map((chart, index) => (
                              <div key={chart.id} className="animate-fadeIn">
                                <InteractiveChart
                                  type={chart.type}
                                  data={chart.data}
                                  title={chart.title}
 insight={chart.insight}
                                />
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
 {/* Actions Assistant Message - Style Claude sobre */}
                {message.role === 'assistant' && (
                  <div className="absolute -bottom-2 right-4 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
 {/* Regenerate Button - Seulement si c'est le dernier message */}
 {messageIndex === messages.length - 1 && !isLoading && (
 <button
 onClick={async () => {
 logger.log('[REGENERATE] Bouton régénérer cliqué');
 
 // SÉCURITÉ : Ne pas régénérer pendant envoi
 if (isSendingRef.current) {
 logger.warn('[REGENERATE] Envoi déjà en cours, annulé');
 return;
 }
 
 // Trouver le dernier message user
 const userMessages = messages.filter(m => m.role === 'user');
 if (userMessages.length === 0) {
 logger.warn('[REGENERATE] Aucun message utilisateur trouvé');
 return;
 }
 
 const lastUserMessage = userMessages[userMessages.length - 1];
 
 logger.log('[REGENERATE] Restauration:', {
 messageId: lastUserMessage.id,
 content: lastUserMessage.content.substring(0, 50) + '...'
 });
 
 // Supprimer CE message assistant (on garde le user)
 const indexOfLastUser = messages.findIndex(m => m.id === lastUserMessage.id);
 const messagesUntilUser = messages.slice(0, indexOfLastUser + 1);
 setMessages(messagesUntilUser);
 
 // Remettre le contenu dans l'input et relancer
 setInputValue(lastUserMessage.content);
 
 // Restaurer fichier si présent
 if (lastUserMessage.metadata?.attachedFile) {
 logger.log('[REGENERATE] Fichier restauré:', lastUserMessage.metadata.attachedFile.name);
 setUploadedFile({
 name: lastUserMessage.metadata.attachedFile.name,
 size: lastUserMessage.metadata.attachedFile.size,
 type: lastUserMessage.metadata.attachedFile.type || '',
 status: 'uploaded',
 fullContent: '',
 fileKey: lastUserMessage.metadata.attachedFile.fileKey,
 fileId: lastUserMessage.metadata.attachedFile.fileId,
 orgPath: lastUserMessage.metadata.attachedFile.orgPath
 });
 }
 
 // Relancer automatiquement (différent du bouton Edit)
 logger.log('[REGENERATE] Envoi AUTO dans 50ms...');
 setTimeout(() => {
 handleSend();
 }, 50);
 }}
 className="p-1.5 bg-theme-surface hover:bg-theme-hover border border-theme-border rounded-md shadow-theme-sm transition-all duration-200 hover:scale-105"
 title="Régénérer"
 >
 <svg className="w-3.5 h-3.5 text-theme-tertiary hover:text-accent-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
 </svg>
 </button>
 )}
 
                    {/* Copy Button */}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(message.content);
                      }}
 className="p-1.5 bg-theme-surface hover:bg-theme-hover border border-theme-border rounded-md shadow-theme-sm transition-all duration-200 hover:scale-105"
                      title="Copier"
                    >
 <Copy className="w-3.5 h-3.5 text-theme-tertiary hover:text-accent-primary transition-colors" />
                    </button>
                    
                    {/* Export Button */}
                    <button
                      onClick={() => {
                        const content = message.thinking ? 
                          `# Analyse Clairify AI\n\n## Réflexion\n${message.thinking}\n\n## Analyse\n${message.content}` :
                          message.content;
                        exportSingleMessage(content, `Clairify_Message_${message.id.slice(-8)}`);
                      }}
 className="p-1.5 bg-theme-surface hover:bg-theme-hover border border-theme-border rounded-md shadow-theme-sm transition-all duration-200 hover:scale-105"
                      title="Exporter"
                    >
 <FileDown className="w-3.5 h-3.5 text-theme-tertiary hover:text-accent-primary transition-colors" />
                    </button>
                  </div>
                )}
              </div>
            </div>
 )}
          </div>
        ))}

 {/* DEEP ANALYSES MODE - Apparaît APRÈS le dernier user message */}
 {thinkingMode && agentSteps.length > 0 && (
 <div className="mb-6">
 <ThinkingAgentV2
 steps={agentSteps}
 currentThinkingContent={currentThinking}
 onContinue={handleAgentContinue}
 isPaused={agentPaused}
 needsConfirmation={needsConfirmation}
 confirmationMessage={confirmationMessage}
 isComplete={!isLoading}
 />
          </div>
        )}

{/* Loading discret et élégant */}
        {isLoading && !currentThinking && (
          <div className="flex justify-start animate-fadeIn mb-4">
            <div className="group relative max-w-xl">
              {/* Container discret avec bordure subtile */}
              <div className="relative overflow-hidden rounded-lg border border-purple-200/30 dark:border-purple-800/30 bg-gradient-to-r from-purple-50/60 to-indigo-50/60 dark:from-purple-950/30 dark:to-indigo-950/30 backdrop-blur-sm shadow-sm">
                {/* Barre de progression discrète */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400 animate-progress opacity-60"></div>
                
                <div className="relative px-4 py-2.5">
                  <div className="flex items-center space-x-3">
                    {/* Icône petite et discrète */}
                    <div className="relative flex-shrink-0">
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-400/20 to-indigo-400/20 dark:from-purple-500/25 dark:to-indigo-500/25 flex items-center justify-center border border-purple-300/40 dark:border-purple-600/40">
                        <Brain className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 animate-pulse" />
                      </div>
                    </div>
                    
                    {/* Texte compact */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Analyse en cours
                        </span>
                        <div className="flex space-x-1">
                          <div className="w-1 h-1 bg-purple-500 dark:bg-purple-400 rounded-full animate-bounce"></div>
                          <div className="w-1 h-1 bg-indigo-500 dark:bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                          <div className="w-1 h-1 bg-blue-500 dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

 {/* Input area - Style OpenAI/Claude - Responsive */}
 <div className="bg-theme-surface border-t border-theme-border p-3 lg:p-4">
        <div className="max-w-4xl mx-auto">
 {/* Boutons de contrôle - Style Claude minimaliste */}
 <div className="flex items-center justify-center flex-wrap gap-1.5 mb-3">
            {/* Upload Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
 disabled={isLoading}
 className={`p-2 rounded-lg border transition-all duration-200 shadow-sm ${
                uploadedFile 
 ? 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border-purple-500/50 text-purple-700 dark:text-purple-300 shadow-purple-500/20' 
 : 'bg-theme-elevated border-theme-border text-theme-text-secondary hover:bg-gradient-to-r hover:from-purple-500/10 hover:to-indigo-500/10 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-500/30'
 } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Uploader un fichier"
            >
 <Paperclip className="w-4 h-4" />
            </button>
            
            {/* Web Search Toggle */}
            <button
              onClick={() => setWebSearchMode(!webSearchMode)}
 disabled={isLoading}
 className={`p-2 rounded-lg border transition-all duration-200 shadow-sm ${
                webSearchMode 
 ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/50 text-blue-700 dark:text-blue-300 shadow-blue-500/20' 
 : 'bg-theme-elevated border-theme-border text-theme-text-secondary hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-cyan-500/10 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-500/30'
 } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Recherche web"
            >
              <Globe className="w-4 h-4" />
            </button>
            
 {/* Deep Analyses */}
            <button
              onClick={() => {
                setThinkingMode(!thinkingMode);
                if (!thinkingMode && quickMode) {
 setQuickMode(false);
 }
 if (thinkingMode) {
 setAgentMode(false);
                }
              }}
 disabled={isLoading}
 className={`relative group p-2 rounded-lg border transition-all duration-200 shadow-sm ${
                thinkingMode 
 ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/50 text-purple-700 dark:text-purple-300 shadow-purple-500/20' 
 : 'bg-theme-elevated border-theme-border text-theme-text-secondary hover:bg-gradient-to-r hover:from-purple-500/10 hover:to-pink-500/10 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-500/30'
 } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
 title="Deep Analyses"
            >
              <Brain className="w-4 h-4" />
            </button>
            
            {/* Quick Mode Toggle */}
            <button
              onClick={() => {
                setQuickMode(!quickMode);
                if (!quickMode && thinkingMode) {
 setThinkingMode(false);
                }
              }}
 disabled={isLoading}
 className={`p-2 rounded-lg border transition-all duration-200 shadow-sm ${
                quickMode 
 ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-amber-500/50 text-amber-700 dark:text-amber-300 shadow-amber-500/20' 
 : 'bg-theme-elevated border-theme-border text-theme-text-secondary hover:bg-gradient-to-r hover:from-amber-500/10 hover:to-yellow-500/10 hover:text-amber-600 dark:hover:text-amber-400 hover:border-amber-500/30'
 } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
 title="Mode rapide"
            >
              <Zap className="w-4 h-4" />
            </button>
            
            {/* Charts Toggle */}
            <button
              onClick={() => setChartsMode(!chartsMode)}
 disabled={isLoading}
 className={`p-2 rounded-lg border transition-all duration-200 shadow-sm ${
                chartsMode 
 ? 'bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border-teal-500/50 text-teal-700 dark:text-teal-300 shadow-teal-500/20' 
 : 'bg-theme-elevated border-theme-border text-theme-text-secondary hover:bg-gradient-to-r hover:from-teal-500/10 hover:to-cyan-500/10 hover:text-teal-600 dark:hover:text-teal-400 hover:border-teal-500/30'
 } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
 title="Graphiques"
            >
              <BarChart3 className="w-4 h-4" />
            </button>
          </div>


 {/* Input principal - Style moderne Light/Dark adapté */}
 <div className="relative bg-theme-surface border border-theme focus-within:border-[var(--theme-text-primary)] pb-safe"
 style={{
 position: 'sticky',
 bottom: 0,
 zIndex: 30,
 }}
 >
 {/* Badge fichier attaché - Style moderne OpenAI/Claude */}
          {uploadedFile && (
 <div className="mx-3 my-2 p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-200 group">
 <div className="flex items-center gap-3">
 {/* Icon */}
 <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
 <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
 </svg>
                </div>
 
 {/* File info */}
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
 {uploadedFile.name}
 </div>
 <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
 {(uploadedFile.size / 1024).toFixed(1)} KB • Prêt à analyser
 </div>
 </div>
 
 {/* Remove button */}
                <button
                  onClick={() => setUploadedFile(null)}
 className="flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors opacity-0 group-hover:opacity-100"
 title="Retirer le fichier"
                >
 <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>
          )}

            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
 placeholder="Posez une question ou importez un fichier…"
 className="w-full bg-transparent text-theme-primary placeholder-theme-tertiary px-4 py-3 pr-14 resize-none border-0 focus:outline-none focus:ring-0 leading-relaxed overflow-y-auto overflow-x-hidden break-words text-base min-h-[52px]"
 style={{ maxHeight: '200px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              rows={1}
              disabled={isLoading}
            />
            
 {/* Bouton Send/Stop - Style Claude sobre */}
 <div className="absolute right-2 bottom-2">
              {isLoading ? (
                <button
 onClick={async () => {
 // Annuler la requête HTTP en cours
 if (abortControllerRef.current) {
 abortControllerRef.current.abort();
 }
 
 // CAPTURER le thinking AVANT de reset (pour éviter duplication)
 const thinkingSnapshot = currentThinking;
 const hasThinking = thinkingSnapshot && thinkingSnapshot.length > 10;
 
 // Reset states IMMÉDIATEMENT
                    setIsLoading(false);
                    setCurrentThinking('');
 setAgentSteps([]);
 setAgentMode(false);
 isSendingRef.current = false;
 
 // SAUVEGARDER le message partial si thinking présent
 if (hasThinking && streamingMessageIdRef.current) {
 setMessages(prev => prev.map(msg => 
 msg.id === streamingMessageIdRef.current 
 ? { ...msg, thinking: thinkingSnapshot }
 : msg
 ));
 
 // Sauvegarder en DB
 if (currentSessionId) {
 try {
 const partialMessage: Message = {
 id: streamingMessageIdRef.current,
 role: 'assistant',
 content: '',
 thinking: thinkingSnapshot,
 timestamp: new Date()
 };
 await saveMessage(currentSessionId, partialMessage);
 } catch (err) {
 logger.error('Erreur sauvegarde partial:', err instanceof Error ? err.message : String(err));
 }
 }
 }
 
 streamingMessageIdRef.current = null;
 }}
 className="p-2 bg-theme-surface hover:bg-theme-hover border border-theme-border text-theme-primary rounded-lg transition-all duration-200 hover:scale-105 shadow-theme-sm"
                  title="Arrêter"
                >
 <Square className="w-4 h-4" />
                </button>
              ) : (
                <button
 onClick={() => {
 logger.log(' [SEND BUTTON] Bouton Send cliqué');
 handleSend();
 }}
 disabled={(!inputValue.trim() && !uploadedFile) || isSendingRef.current || (uploadedFile && uploadedFile.status === 'uploading')}
 className={`p-3 ${
 (inputValue.trim() || uploadedFile) && !isSendingRef.current && !(uploadedFile && uploadedFile.status === 'uploading')
 ? 'bg-[var(--theme-text-primary)] text-[var(--theme-text-inverse)]'
 : 'bg-theme-hover text-theme-disabled cursor-not-allowed'
                  }`}
                  title={
                    uploadedFile && uploadedFile.status === 'uploading' 
                      ? 'Traitement du fichier en cours...' 
                      : isSendingRef.current 
                        ? 'Envoi en cours...' 
                        : (!inputValue.trim() && !uploadedFile)
                          ? 'Saisissez un message ou uploadez un fichier'
                          : 'Envoyer'
                  }
                >
 <Send className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.json"
            onChange={handleFileUpload}
            className="hidden"
          />
          
 <div className="text-xs text-theme-secondary mt-2 text-center">
 <span className="hidden sm:inline">Entrée pour envoyer · Shift+Entrée pour nouvelle ligne</span>
 <span className="sm:hidden">Entrée = envoyer</span>
          </div>
        </div>
      </div>
      </div>

 {/* Modal Tokens Insuffisants */}
 <InsufficientTokensModal
 isOpen={showInsufficientTokensModal}
 onClose={() => setShowInsufficientTokensModal(false)}
 currentBalance={balance}
 requiredTokens={requiredTokensForRequest}
 />
    </div>
  );
};

export default FluidChat;